import { cn } from "./classNames-BzrkDnOD.js";
import { Tooltip, TooltipProvider } from "./utils-Bd28bWgm.js";
import { AlertCircle, ArrowDown, ArrowLeft, ArrowRight, ArrowUp, Ban, BarChart3, Bell, Bug, Calendar, Check, ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Circle, CircleAlert, CircleCheck, CircleHelp, CirclePause, CirclePlay, CirclePlus, CircleStop, CircleX, Clock, Cog, Copy, Database, Download, Ellipsis, ExternalLink, Eye, File, FilePen, FileText, Flag, Folder, Gauge, GitBranch, GitMerge, GitPullRequest, Globe, Hand, History, Hourglass, Info, Key, Layers, LineChart, Link, List, ListChecks, Loader2, Lock, Mail, Maximize2, Minimize2, Minus, Pencil, Play, Plus, RefreshCw, Rocket, RotateCcw, RotateCw, Save, Search, Send, Settings, Sparkles, Star, Table, ThumbsDown, ThumbsUp, Trash2, TriangleAlert, Upload, User, Users, WandSparkles, Warehouse, Workflow, X, XCircle, Zap } from "./icons-wcA70iQx.js";
import { Button } from "./Button-B21oVcPU.js";
import { ButtonGroup } from "./internal/components/ButtonGroup.js";
import { ClipboardButton } from "./ClipboardButton-srUgWgwH.js";
import { FullScreenModal, FullScreenModalBody, FullScreenModalHeader } from "./FullScreenModal-Cwnm8WdM.js";
import { Truncated } from "./internal/components/Truncated.js";
import { HoverCard } from "./HoverCard-B8OKu9XZ.js";
import { Command } from "./Command-BQPiZCNz.js";
import { TextInput } from "./TextInput-BWYXbfs4.js";
import { Badge } from "./Badge-Dz0cj6rj.js";
import { Textarea } from "./internal/blocks/Textarea.js";
import { Table as Table$1 } from "./Table-BaVT5Tkn.js";
import { DiagramSkeleton } from "./internal/blocks/DiagramShared/DiagramSkeleton.js";
import { DiagramSource } from "./DiagramSource-C5_d8Gdj.js";
import { Kbd, keys } from "./Kbd-J4n1J6i4.js";
import { Progress } from "./internal/components/Progress.js";
import { digitFromKeyCode, useAltDigitHotkey } from "./internal/hooks/useAltDigitHotkey.js";
import { Slot } from "@radix-ui/react-slot";
import { createContext, isValidElement, use, useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { cva } from "class-variance-authority";
import { Fragment as Fragment$1, jsx, jsxs } from "react/jsx-runtime";
import TextareaAutosize from "react-textarea-autosize";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
//#region src/blocks/AIKit/AnswerActions.tsx
const VOTE_BUTTONS = [{
	vote: "up",
	icon: /* @__PURE__ */ jsx(ThumbsUp, { className: "tw:size-4" })
}, {
	vote: "down",
	icon: /* @__PURE__ */ jsx(ThumbsDown, { className: "tw:size-4" })
}];
const DEFAULT_LABELS = {
	up: "Good answer",
	down: "Bad answer"
};
/** Roomier for a panel; compact for a dense transcript. */
const SIZES = {
	small: {
		button: "icon-xs",
		gap: "extra-small"
	},
	medium: {
		button: "icon-sm",
		gap: "little-small"
	}
};
/**
* Copy and thumbs up/down for a finished answer.
*
* - Always rendered, never hover-only, so it is reachable without a pointer.
* - A vote can be changed but not withdrawn; pressing the pressed one re-sends it.
* - The caller owns the recorded vote and the write.
*/
const AnswerActions = ({ copyText, vote, onVote, onCopy, actions, labels = DEFAULT_LABELS, confirmation, size = "small", layout = "inline" }) => {
	const { button, gap } = SIZES[size];
	const votes = VOTE_BUTTONS.map(({ vote: candidate, icon }) => {
		const isRecorded = vote === candidate;
		const label = labels[candidate];
		return /* @__PURE__ */ jsx(Tooltip, {
			tooltip: label,
			placement: "top",
			triggerAsChild: true,
			children: /* @__PURE__ */ jsx(Button, {
				size: button,
				variant: "invisible",
				className: cn(isRecorded && candidate === "up" && "tw:bg-success/10 tw:text-success tw:hover:not-disabled:bg-success/20 tw:hover:not-disabled:text-success", isRecorded && candidate === "down" && "tw:bg-destructive/10 tw:text-destructive tw:hover:not-disabled:bg-destructive/20 tw:hover:not-disabled:text-destructive"),
				"aria-label": label,
				"aria-pressed": isRecorded,
				onClick: () => onVote(candidate),
				children: icon
			})
		}, candidate);
	});
	const copy = copyText ? /* @__PURE__ */ jsx(ClipboardButton, {
		text: copyText,
		tooltip: "Copy answer",
		size: button,
		variant: "invisible",
		onClick: onCopy
	}) : null;
	const said = confirmation ? /* @__PURE__ */ jsx("span", {
		"aria-live": "polite",
		className: "tw:ml-1 tw:text-xs tw:font-medium tw:text-foreground tw:empty:hidden tw:animate-in tw:fade-in tw:slide-in-from-left-1 tw:duration-300",
		children: vote ? confirmation[vote] : ""
	}) : null;
	if (layout === "split") return /* @__PURE__ */ jsxs("div", {
		className: "tw:flex tw:w-full tw:items-center tw:justify-between tw:gap-2",
		children: [/* @__PURE__ */ jsxs(ButtonGroup, {
			space: gap,
			"aria-label": "Answer actions",
			children: [votes, said]
		}), /* @__PURE__ */ jsxs(ButtonGroup, {
			space: gap,
			children: [actions, copy]
		})]
	});
	return /* @__PURE__ */ jsxs(ButtonGroup, {
		space: gap,
		"aria-label": "Answer actions",
		children: [
			copy,
			votes,
			actions,
			said
		]
	});
};
//#endregion
//#region src/blocks/AIKit/CollapsibleBody.tsx
const CollapsibleBody = ({ isExpanded, isClosing, onClosed, children }) => {
	const handleTransitionEnd = (event) => {
		if (event.target !== event.currentTarget) return;
		if (event.propertyName !== "grid-template-rows") return;
		if (!isExpanded) onClosed();
	};
	return /* @__PURE__ */ jsx("div", {
		className: cn("tw:grid tw:transition-[grid-template-rows] tw:duration-300 tw:ease-out tw:motion-reduce:transition-none", isExpanded ? "tw:grid-rows-[1fr]" : "tw:grid-rows-[0fr]"),
		onTransitionEnd: handleTransitionEnd,
		children: /* @__PURE__ */ jsx("div", {
			className: "tw:min-h-0 tw:overflow-hidden",
			children: (isExpanded || isClosing) && children
		})
	});
};
//#endregion
//#region src/blocks/AIKit/CollapsibleStepHeader.tsx
const variantClasses = {
	success: "tw:text-muted-foreground tw:focus-visible:outline-ring tw:not-disabled:hover:text-foreground",
	error: "tw:text-destructive tw:focus-visible:outline-destructive tw:not-disabled:hover:text-destructive",
	processing: "tw:text-muted-foreground tw:focus-visible:outline-ring tw:not-disabled:hover:text-foreground",
	dismissed: "tw:text-muted-foreground tw:focus-visible:outline-ring tw:not-disabled:hover:text-foreground"
};
const iconClasses = "tw:size-3.5 tw:shrink-0 tw:basis-3.5 tw:opacity-70";
const variantStatus = {
	success: "finished",
	error: "failed",
	processing: "running",
	dismissed: "not run"
};
const renderVariantIcon$1 = (variant) => {
	if (variant === "processing") return /* @__PURE__ */ jsx(Loader2, { className: cn(iconClasses, "tw:animate-spin") });
	if (variant === "error" || variant === "dismissed") return /* @__PURE__ */ jsx(XCircle, { className: iconClasses });
	return /* @__PURE__ */ jsx(CircleCheck, { className: iconClasses });
};
const CollapsibleStepHeader = ({ isExpanded, onToggle, label, variant = "success" }) => {
	return /* @__PURE__ */ jsxs("button", {
		type: "button",
		"aria-expanded": isExpanded,
		onClick: onToggle,
		"data-variant": variant,
		className: cn("tw:flex tw:w-fit tw:items-center tw:gap-1.5 tw:text-left tw:text-xs tw:font-medium tw:transition-colors tw:focus-visible:outline tw:focus-visible:outline-2 tw:focus-visible:outline-offset-2 tw:disabled:opacity-40", variantClasses[variant]),
		children: [renderVariantIcon$1(variant), /* @__PURE__ */ jsxs("span", {
			className: "tw:min-w-0",
			children: [
				label,
				/* @__PURE__ */ jsx("span", {
					className: "tw:sr-only",
					children: ` (${variantStatus[variant]})`
				}),
				/* @__PURE__ */ jsxs("span", {
					className: "tw:whitespace-nowrap",
					children: ["⁠", /* @__PURE__ */ jsx(ChevronDown, { className: cn("tw:ml-1.5 tw:inline-block tw:size-3.5 tw:align-middle tw:transition-transform", isExpanded && "tw:rotate-180") })]
				})
			]
		})]
	});
};
//#endregion
//#region src/blocks/AIKit/hasMotion.ts
const hasMotion = () => typeof window.matchMedia === "function" && !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
//#endregion
//#region src/blocks/AIKit/InlineStepDetail.tsx
const InlineStepDetail = ({ children }) => /* @__PURE__ */ jsx("div", {
	className: "tw:ml-2.5 tw:whitespace-pre-wrap tw:border-l tw:border-border tw:pl-3 tw:text-xs tw:text-muted-foreground",
	children
});
//#endregion
//#region src/blocks/AIKit/InlineStepLabel.tsx
const renderVariantIcon = (variant) => {
	if (variant === "processing") return /* @__PURE__ */ jsx(Loader2, { className: "tw:size-3.5 tw:animate-spin tw:text-muted-foreground" });
	if (variant === "error") return /* @__PURE__ */ jsx(XCircle, { className: "tw:size-3.5 tw:text-destructive" });
	if (variant === "dismissed") return /* @__PURE__ */ jsx(XCircle, { className: "tw:size-3.5 tw:text-muted-foreground/50" });
	return /* @__PURE__ */ jsx(CircleCheck, { className: "tw:size-3.5 tw:text-muted-foreground/50" });
};
const InlineStepLabel = ({ variant, children }) => /* @__PURE__ */ jsxs("div", {
	className: "tw:flex tw:min-w-0 tw:items-center tw:gap-1",
	children: [/* @__PURE__ */ jsx("div", {
		className: "tw:relative tw:flex tw:size-5 tw:shrink-0 tw:items-center tw:justify-center tw:[background:var(--inline-step-surface,transparent)]",
		children: renderVariantIcon(variant)
	}), /* @__PURE__ */ jsx("div", {
		className: "tw:flex tw:min-w-0 tw:flex-wrap tw:items-center tw:gap-x-1.5 tw:gap-y-1 tw:text-xs tw:font-medium tw:text-muted-foreground",
		children
	})]
});
//#endregion
//#region src/blocks/AIKit/AnswerSummary.tsx
const SECTION_LABELS = {
	success: "Answered questions",
	dismissed: "Dismissed questions"
};
const AnswerSummary = ({ items, variant = "success", noAnswerLabel = "No answer", onExpandToggle }) => {
	const [isExpanded, setIsExpanded] = useState(false);
	const [isClosing, setIsClosing] = useState(false);
	const toggle = () => {
		const next = !isExpanded;
		if (!next) setIsClosing(hasMotion());
		setIsExpanded(next);
		onExpandToggle?.(next);
	};
	const count = items.length;
	const [firstItem] = items;
	const label = count === 1 && firstItem ? firstItem.question : `${count} questions`;
	return /* @__PURE__ */ jsxs("section", {
		className: "tw:mt-4 tw:flex tw:flex-col",
		"aria-label": SECTION_LABELS[variant],
		children: [/* @__PURE__ */ jsx(CollapsibleStepHeader, {
			isExpanded,
			onToggle: toggle,
			variant,
			label
		}), /* @__PURE__ */ jsx(CollapsibleBody, {
			isExpanded,
			isClosing,
			onClosed: () => setIsClosing(false),
			children: /* @__PURE__ */ jsx("div", {
				className: "tw:flex tw:flex-col tw:gap-4 tw:pt-4 tw:pl-5",
				children: items.map(({ question, answer }, index) => /* @__PURE__ */ jsxs("div", {
					className: "tw:flex tw:flex-col tw:gap-1",
					children: [/* @__PURE__ */ jsx(InlineStepLabel, {
						variant,
						children: question
					}), /* @__PURE__ */ jsx(InlineStepDetail, { children: answer && answer.length > 0 ? answer : noAnswerLabel })]
				}, `${index}-${question}`))
			})
		})]
	});
};
//#endregion
//#region src/blocks/AIKit/ChatInlineIcon.tsx
/**
* The Lucide component each `icon:<name>` token draws.
*
* - Names are FontAwesome-solid ones, published as `CHAT_INLINE_ICON_NAMES`.
* - The `satisfies` is the drift guard: a name the prompt teaches and this map lacks fails to compile.
* - Read through `Object.hasOwn`: `icon:constructor` would otherwise hit `Object.prototype`.
*/
const ICON_REGISTRY = {
	"alert-circle": AlertCircle,
	"arrow-down": ArrowDown,
	"arrow-left": ArrowLeft,
	"arrow-right": ArrowRight,
	"arrow-rotate-back": RotateCcw,
	"arrow-rotate-left": RotateCcw,
	"arrow-rotate-right": RotateCw,
	"arrow-up": ArrowUp,
	"arrow-up-right-from-square": ExternalLink,
	"arrows-rotate": RefreshCw,
	analytics: BarChart3,
	ban: Ban,
	bell: Bell,
	bug: Bug,
	calendar: Calendar,
	changes: FilePen,
	"chart-line": LineChart,
	check: Check,
	"check-circle": CircleCheck,
	"chevron-down": ChevronDown,
	"chevron-left": ChevronLeft,
	"chevron-right": ChevronRight,
	"chevron-up": ChevronUp,
	circle: Circle,
	"circle-check": CircleCheck,
	"circle-exclamation": CircleAlert,
	"circle-info": Info,
	"circle-pause": CirclePause,
	"circle-play": CirclePlay,
	"circle-plus": CirclePlus,
	"circle-question": CircleHelp,
	"circle-stop": CircleStop,
	"circle-xmark": CircleX,
	clock: Clock,
	"clock-rotate-left": History,
	"code-branch": GitBranch,
	"code-merge": GitMerge,
	"code-pull-request": GitPullRequest,
	cog: Cog,
	configuration: Settings,
	copy: Copy,
	database: Database,
	"diagram-project": Workflow,
	download: Download,
	ellipsis: Ellipsis,
	envelope: Mail,
	"exclamation-circle": CircleAlert,
	"exclamation-triangle": TriangleAlert,
	eye: Eye,
	file: File,
	"file-lines": FileText,
	flag: Flag,
	folder: Folder,
	gauge: Gauge,
	gear: Settings,
	globe: Globe,
	hand: Hand,
	"hourglass-half": Hourglass,
	"info-circle": Info,
	key: Key,
	"layer-group": Layers,
	link: Link,
	"list-check": ListChecks,
	"list-ul": List,
	lock: Lock,
	"magnifying-glass": Search,
	minus: Minus,
	"paper-plane-top": Send,
	pen: Pencil,
	play: Play,
	plus: Plus,
	refresh: RefreshCw,
	rocket: Rocket,
	save: Save,
	"save-changes": Save,
	search: Search,
	spinner: Loader2,
	sparkles: Sparkles,
	star: Star,
	stop: CircleStop,
	table: Table,
	"thumbs-down": ThumbsDown,
	"thumbs-up": ThumbsUp,
	transformation: WandSparkles,
	trash: Trash2,
	"triangle-exclamation": TriangleAlert,
	upload: Upload,
	user: User,
	"user-group": Users,
	warning: TriangleAlert,
	warehouse: Warehouse,
	xmark: X,
	zap: Zap
};
/**
* Extra classes a single name needs.
*
* - `spinner` spins (per `MAINTAINING.md`): a still `Loader2` reads as a broken ring.
* - Read through `Object.hasOwn`, like the registry.
*/
const ICON_CLASSES = { spinner: "tw:animate-spin" };
/**
* A whole `icon:<name>` token and nothing else.
*
* - Must match the name shape `remarkIconSyntax` (`@keboola/kai-chat`) accepts.
* - Anchored, so inline code like `` `icon: database` `` or `` `icon:` `` stays code.
*/
const TOKEN = /^icon:([a-z][a-z0-9-]*)$/;
/** Built once: an answer is re-parsed per streamed chunk, so `isArriving` runs per code node. */
const ICON_NAMES = Object.keys(ICON_REGISTRY);
/** Whether a name is a strict prefix of a registry name: `tabl` → `true`, `table-name` → `false`. */
const isArriving = (name) => ICON_NAMES.some((key) => key.length > name.length && key.startsWith(name));
/**
* The icon name in a code node's text, if the text is a token — drawn, or still arriving.
*
* - An unknown name → `undefined`, so the call site's `<code>` fall-through keeps the text.
* - A prefix of a known name is a token still streaming; `<ChatInlineIcon />` draws it blank.
* - A name that is both (`save`, of `save-changes`) is drawn now.
*/
const getChatInlineIconName = (text) => {
	const name = TOKEN.exec(text)?.[1];
	if (name === void 0) return void 0;
	return Object.hasOwn(ICON_REGISTRY, name) || isArriving(name) ? name : void 0;
};
/**
* One inline `icon:<name>` token, drawn at the size of the text it leads.
*
* - An unknown name (a token still arriving) renders nothing.
* - `mr` widens the single source space, which reads as cramped beside a glyph.
* - The heavier stroke holds its own beside a bold heading.
* - `aria-hidden`: the token's text is removed, so the glyph has no label to announce.
*/
const ChatInlineIcon = ({ name }) => {
	const Icon = Object.hasOwn(ICON_REGISTRY, name) ? ICON_REGISTRY[name] : void 0;
	if (Icon === void 0) return null;
	return /* @__PURE__ */ jsx(Icon, {
		"aria-hidden": true,
		className: cn("tw:mr-[0.15em] tw:inline-block tw:size-[1em] tw:shrink-0 tw:align-[-0.125em] tw:text-muted-foreground", Object.hasOwn(ICON_CLASSES, name) ? ICON_CLASSES[name] : void 0),
		strokeWidth: 2.25
	});
};
//#endregion
//#region src/blocks/AIKit/FileDownloadChip.tsx
/**
* A file the agent wrote, offered inside its own answer.
*
* - The whole chip is the control: a small icon beside a truncating filename is hard to hit.
* - Presentation only — the host fetches and saves, through its own auth.
*/
const FileDownloadChip = ({ fileName, filePath, size, isPending = false, onDownload, className }) => /* @__PURE__ */ jsxs(Button, {
	variant: "secondary",
	size: "small",
	isLoading: isPending,
	"aria-disabled": isPending,
	"aria-label": `Download ${filePath ?? fileName}`,
	title: filePath,
	onClick: () => {
		if (!isPending) onDownload();
	},
	className: cn("tw:max-w-full tw:justify-start tw:gap-2 tw:font-normal", className),
	children: [
		!isPending && /* @__PURE__ */ jsx(FileText, { className: "tw:size-4 tw:shrink-0 tw:text-muted-foreground" }),
		/* @__PURE__ */ jsx("span", {
			className: "tw:truncate",
			children: fileName
		}),
		size !== void 0 && /* @__PURE__ */ jsx("span", {
			className: "tw:shrink-0 tw:text-xs tw:text-muted-foreground",
			children: size
		}),
		!isPending && /* @__PURE__ */ jsx(Download, { className: "tw:size-4 tw:shrink-0" })
	]
});
//#endregion
//#region src/blocks/AIKit/InlineStepGroup.tsx
/** The surface colour, as the custom property the step icons mask against. */
const surfaceStyle = (surface) => ({ "--inline-step-surface": surface });
/**
* A collapsible run of agent steps behind one summary line; rows arrive as children.
*
* - Connector sits at the icon centre line (`pl-5` + half a `size-5` box), drawn once so it
*   survives icon-less rows.
* - Animates `grid-template-rows`; children stay mounted while closed.
* - `role="group"`, not a `region` landmark: dozens per transcript would drown real landmarks.
*/
const InlineStepGroup = ({ label, variant = "success", isExpanded, onToggle, surface = "rgb(var(--background))", "aria-label": ariaLabel, "data-testid": testId, className, children }) => /* @__PURE__ */ jsxs("section", {
	role: "group",
	className: cn("tw:flex tw:flex-col", className),
	"aria-label": ariaLabel,
	"data-testid": testId,
	children: [/* @__PURE__ */ jsx(CollapsibleStepHeader, {
		isExpanded,
		onToggle,
		label,
		variant
	}), /* @__PURE__ */ jsx("div", {
		className: cn("tw:grid tw:transition-[grid-template-rows] tw:duration-200 tw:ease-out tw:motion-reduce:transition-none", isExpanded ? "tw:grid-rows-[1fr]" : "tw:grid-rows-[0fr]"),
		children: /* @__PURE__ */ jsx("div", {
			className: "tw:min-h-0 tw:overflow-hidden",
			inert: !isExpanded,
			"aria-hidden": isExpanded ? void 0 : true,
			children: /* @__PURE__ */ jsxs("div", {
				style: surfaceStyle(surface),
				className: "tw:relative tw:flex tw:flex-col tw:gap-4 tw:pt-4 tw:pl-5",
				children: [/* @__PURE__ */ jsx("span", {
					"aria-hidden": "true",
					className: "tw:pointer-events-none tw:absolute tw:top-4 tw:bottom-0 tw:left-[30px] tw:w-px tw:bg-border"
				}), children]
			})
		})
	})]
});
//#endregion
//#region src/blocks/AIKit/DiagramBlock.tsx
/**
* One diagram block: a skeleton while its chunk loads, the fence source plus a retry if it fails.
*
* - Each attempt is a fresh fetch; the loader drops its rejected promise and logs the failure.
* - Retry is manual: a chunk gone after a deploy never returns, so a timer would loop forever.
* - A new `load` resets the block, or a dialect switch keeps the previous renderer.
* - Functions in state go through the setter form, or React calls them.
*/
const DiagramBlock = ({ load, code }) => {
	const [Diagram, setDiagram] = useState(null);
	const [failed, setFailed] = useState(false);
	const [attempt, setAttempt] = useState(0);
	const [prevLoad, setPrevLoad] = useState(() => load);
	if (load !== prevLoad) {
		setPrevLoad(() => load);
		setDiagram(null);
		setFailed(false);
	}
	useEffect(() => {
		let cancelled = false;
		load().then((component) => {
			if (!cancelled) setDiagram(() => component);
		}, () => {
			if (!cancelled) setFailed(true);
		});
		return () => {
			cancelled = true;
		};
	}, [load, attempt]);
	if (Diagram) return /* @__PURE__ */ jsx(Diagram, { code });
	if (failed) return /* @__PURE__ */ jsxs("div", { children: [/* @__PURE__ */ jsx(DiagramSource, { code }), /* @__PURE__ */ jsx(Button, {
		variant: "inline",
		size: "small",
		className: "tw:mb-3",
		onClick: () => {
			setFailed(false);
			setAttempt((count) => count + 1);
		},
		children: "Try again"
	})] });
	return /* @__PURE__ */ jsx(DiagramSkeleton, {});
};
//#endregion
//#region src/blocks/AIKit/diagramChunk.ts
/**
* An on-demand diagram chunk, shared between mounts and retryable.
*
* - Not `lazy()`: it memoizes a failed import, stranding every diagram until a reload.
* - A resolved import is kept; a failed one is cleared so the next mount retries.
* - Same contract as `getFlintRuntime()` in `FlintChart`.
* - Failures are logged here, where the chunk's name is known.
*/
const diagramChunk = (name, load) => {
	let pending = null;
	return () => {
		pending ??= load().catch((error) => {
			console.error(`[AIKit] failed to load the ${name} chunk`, error);
			pending = null;
			throw error;
		});
		return pending;
	};
};
//#endregion
//#region src/blocks/AIKit/Markdown.tsx
const loadMermaid = diagramChunk("MermaidDiagram", () => import("@keboola/design/mermaid").then((module) => module.MermaidDiagram));
const loadFlint = diagramChunk("FlintChart", () => import("@keboola/design/flint").then((module) => module.FlintChart));
const PROSE_CLASS = {
	answer: "tw:prose tw:prose-answer tw:prose-code-chip tw:break-words",
	chrome: "tw:prose tw:prose-sm tw:prose-code-chip tw:break-words"
};
const getText = (node) => {
	if (typeof node === "string") return node;
	if (typeof node === "number") return String(node);
	if (Array.isArray(node)) return node.map(getText).join("");
	if (isValidElement(node) && typeof node.props === "object" && node.props !== null && "children" in node.props) return getText(node.props.children);
	return "";
};
const getLanguage = (element) => {
	if (!isValidElement(element)) return void 0;
	const { props } = element;
	if (typeof props !== "object" || props === null || !("className" in props)) return void 0;
	if (typeof props.className !== "string") return void 0;
	return props.className.match(/language-(\S+)/)?.[1];
};
/** Always run; a caller's plugins are appended, never substituted. */
const baseRemarkPlugins = [remarkGfm];
/**
* Fence language → the on-demand chunk that draws it.
*
* - Dialects come from kai-agent's prompt; keep in sync with kbc-ui `markdownComponents.tsx`.
* - `flint-chart`: alias models emit unprompted.
* - Read via `Object.hasOwn`: ```constructor would otherwise hit `Object.prototype` and throw.
*/
const DIAGRAM_CHUNKS = {
	mermaid: loadMermaid,
	flint: loadFlint,
	"flint-chart": loadFlint
};
/**
* The fence languages this renderer draws instead of printing.
*
* - Streaming callers hold an open fence back as plain code, or every keystroke fetches the chunk.
* - Frozen: it is shared across every app in the bundle.
*/
const DIAGRAM_LANGUAGES = Object.freeze(Object.keys(DIAGRAM_CHUNKS));
/**
* Whether the `code` being rendered sits inside a fenced block.
*
* - Set by `pre`: a fence without info string has no `language-…` class, like inline code.
*/
const CodeBlockContext = createContext(false);
/**
* The `code` renderer: an inline `icon:<name>` token becomes the icon it names.
*
* - Fenced blocks never swap.
* - Unknown names stay code; a name still streaming renders nothing.
*/
const MarkdownCode = ({ children, className, ...props }) => {
	const { node: _node, ...rest } = props;
	const name = use(CodeBlockContext) ? void 0 : getChatInlineIconName(getText(children));
	if (name !== void 0) return /* @__PURE__ */ jsx(ChatInlineIcon, { name });
	return /* @__PURE__ */ jsx("code", {
		className,
		...rest,
		children
	});
};
const baseComponents = {
	pre({ children, ...props }) {
		const child = Array.isArray(children) ? children[0] : children;
		const code = getText(child).replace(/\n$/, "");
		const language = getLanguage(child);
		const load = language !== void 0 && Object.hasOwn(DIAGRAM_CHUNKS, language) ? DIAGRAM_CHUNKS[language] : void 0;
		if (load) {
			if (!code.trim()) return null;
			return /* @__PURE__ */ jsx(DiagramBlock, {
				load,
				code
			});
		}
		const { node: _node, ...rest } = props;
		return /* @__PURE__ */ jsxs("div", {
			className: "tw:group tw:relative",
			children: [/* @__PURE__ */ jsx("span", {
				className: "tw:absolute tw:right-2 tw:top-2 tw:opacity-0 tw:transition-opacity tw:group-focus-within:opacity-100 tw:group-hover:opacity-100",
				children: /* @__PURE__ */ jsx(ClipboardButton, {
					text: code,
					tooltip: "Copy",
					size: "icon-xs",
					variant: "outline"
				})
			}), /* @__PURE__ */ jsx("pre", {
				...rest,
				children: /* @__PURE__ */ jsx(CodeBlockContext, {
					value: true,
					children
				})
			})]
		});
	},
	code: MarkdownCode,
	a({ children, href, ...props }) {
		const { node: _node, ...rest } = props;
		const isExternal = !!href && /^https?:\/\//.test(href);
		return /* @__PURE__ */ jsx("a", {
			href,
			...isExternal ? {
				target: "_blank",
				rel: "noopener noreferrer"
			} : null,
			...rest,
			children
		});
	},
	table({ className, ...props }) {
		const { node: _node, ...rest } = props;
		return /* @__PURE__ */ jsx("div", {
			className: "tw:not-prose tw:my-4",
			children: /* @__PURE__ */ jsx(Table$1, {
				bordered: true,
				className: cn("tw:!mb-0", className),
				...rest
			})
		});
	},
	thead(props) {
		const { node: _node, ...rest } = props;
		return /* @__PURE__ */ jsx(Table$1.Header, { ...rest });
	},
	tbody(props) {
		const { node: _node, ...rest } = props;
		return /* @__PURE__ */ jsx(Table$1.Body, { ...rest });
	},
	tr(props) {
		const { node: _node, ...rest } = props;
		return /* @__PURE__ */ jsx(Table$1.Row, { ...rest });
	},
	th(props) {
		const { node: _node, ...rest } = props;
		return /* @__PURE__ */ jsx(Table$1.Head, { ...rest });
	},
	td(props) {
		const { node: _node, ...rest } = props;
		return /* @__PURE__ */ jsx(Table$1.Cell, { ...rest });
	}
};
/**
* The prose surface an answer is drawn on.
*
* - Wrap all `Block`s of one answer in one `Root`, so `:first-child`/`:last-child` span the reply.
* - `data-prose` is `MessageBubble`'s colour hook; Tailwind can't target `tw:prose` directly.
* - Spacing overrides need `!`: `tw:prose-answer` rules are `(0,2,1)`, above `[&_p]:mb-1`.
*/
const MarkdownRoot = ({ children, className, rhythm = "answer" }) => /* @__PURE__ */ jsx("div", {
	"data-prose": true,
	className: cn(PROSE_CLASS[rhythm], className),
	children
});
/** One parsed piece of an answer, with no surface of its own; memoise at the call site. */
const MarkdownBlock = ({ children, remarkPlugins, components: componentOverrides }) => /* @__PURE__ */ jsx(ReactMarkdown, {
	remarkPlugins: remarkPlugins === void 0 ? baseRemarkPlugins : [...baseRemarkPlugins, ...remarkPlugins],
	components: componentOverrides === void 0 ? baseComponents : {
		...baseComponents,
		...componentOverrides
	},
	children
});
/**
* Read-only GFM Markdown renderer with design-token typography.
*
* - Needs a `TooltipProvider` at the app root (code copy button).
* - Pass `rhythm="chrome"` for content a model did not write.
*/
const Markdown = ({ className, rhythm, ...blockProps }) => /* @__PURE__ */ jsx(MarkdownRoot, {
	className,
	rhythm,
	children: /* @__PURE__ */ jsx(MarkdownBlock, { ...blockProps })
});
Markdown.Root = MarkdownRoot;
Markdown.Block = MarkdownBlock;
//#endregion
//#region src/blocks/AIKit/MarkdownTable.tsx
/** How much wider than its column a table has to be before the column is the wrong surface. */
const EXPAND_RATIO = 1.5;
/**
* The fade's end colour — must match the host's surface, or it draws a strip over the data.
*
* - Full class strings: Tailwind reads literals.
*/
const FADE_SURFACE = {
	card: "tw:from-card",
	background: "tw:from-background"
};
/**
* A table in an answer that shows when it is cut off on the right.
*
* - Controls sit in a fixed-height rail above the grid, so they never cover rows or shift them.
* - The fade shows only while content remains to the right.
* - A painted gradient, not `tw:scroll-fade-x`: that needs scroll-driven animations, which the
*   `baseline-widely-available` build floor lacks, and must sit on `Table`'s own container.
* - Past `EXPAND_RATIO` the expand action shows without hover.
*/
const MarkdownTable = ({ children, actions, onExpand, surface = "card", className }) => {
	const frameRef = useRef(null);
	const [overflow, setOverflow] = useState({
		scrollable: false,
		atEnd: true,
		ratio: 1
	});
	const measure = useCallback(() => {
		const box = frameRef.current?.firstElementChild;
		if (!box) return;
		const { scrollLeft, scrollWidth, clientWidth } = box;
		const hidden = scrollWidth - clientWidth;
		setOverflow({
			scrollable: hidden > 1,
			atEnd: scrollLeft >= hidden - 1,
			ratio: clientWidth > 0 ? scrollWidth / clientWidth : 1
		});
	}, []);
	useEffect(() => {
		const box = frameRef.current?.firstElementChild;
		if (!box) return;
		measure();
		box.addEventListener("scroll", measure, { passive: true });
		const observer = new ResizeObserver(measure);
		observer.observe(box);
		if (box.firstElementChild) observer.observe(box.firstElementChild);
		return () => {
			box.removeEventListener("scroll", measure);
			observer.disconnect();
		};
	}, [measure]);
	const showExpand = !!onExpand && overflow.scrollable && overflow.ratio >= EXPAND_RATIO;
	return /* @__PURE__ */ jsxs("div", {
		className,
		children: [(actions || onExpand) && /* @__PURE__ */ jsxs("div", {
			className: "tw:flex tw:h-8 tw:items-center tw:justify-end tw:gap-1",
			children: [actions, showExpand && /* @__PURE__ */ jsx(Tooltip, {
				tooltip: "This table is wider than the space it has",
				placement: "top",
				triggerAsChild: true,
				children: /* @__PURE__ */ jsxs(Button, {
					size: "small",
					variant: "invisible",
					onClick: onExpand,
					children: [/* @__PURE__ */ jsx(Maximize2, { className: "tw:size-3.5" }), "Open wider"]
				})
			})]
		}), /* @__PURE__ */ jsxs("div", {
			ref: frameRef,
			className: "tw:relative",
			children: [children, overflow.scrollable && !overflow.atEnd && /* @__PURE__ */ jsx("span", {
				"aria-hidden": true,
				"data-testid": "markdown-table-fade",
				className: cn("tw:pointer-events-none tw:absolute tw:inset-y-px tw:right-px tw:w-12 tw:rounded-r-[10px] tw:bg-gradient-to-l tw:to-transparent", FADE_SURFACE[surface])
			})]
		})]
	});
};
//#endregion
//#region src/blocks/AIKit/MessageBubble.tsx
const containerClasses = cva([
	"tw:flex",
	"tw:max-w-full",
	"tw:gap-3"
], {
	variants: { variant: {
		user: ["tw:justify-end"],
		assistant: ["tw:justify-start", "tw:w-full"]
	} },
	defaultVariants: { variant: "assistant" }
});
const stackClasses = cva([
	"tw:flex",
	"tw:min-w-0",
	"tw:flex-1",
	"tw:flex-col",
	"tw:gap-2"
], {
	variants: { variant: {
		user: ["tw:items-end"],
		assistant: []
	} },
	defaultVariants: { variant: "assistant" }
});
const contentClasses = cva([
	"tw:flex",
	"tw:flex-col",
	"tw:gap-3",
	"tw:break-words",
	"tw:leading-relaxed",
	"tw:text-foreground",
	"tw:[&_[data-prose]]:text-foreground"
], {
	variants: { variant: {
		assistant: [
			"tw:flex-1",
			"tw:w-full",
			"tw:min-w-0",
			"tw:[&>*]:min-w-0"
		],
		user: [
			"tw:max-w-[min(85%,40rem)]",
			"tw:w-fit",
			"tw:bg-muted",
			"tw:px-3",
			"tw:py-2",
			"tw:rounded-xl",
			"tw:overflow-hidden",
			"tw:wrap-anywhere"
		]
	} },
	defaultVariants: { variant: "assistant" }
});
const MessageBubble = ({ variant, children, actions, createdAt }) => {
	return /* @__PURE__ */ jsx("div", {
		className: containerClasses({ variant }),
		"data-testid": `chat-message-${variant}`,
		children: /* @__PURE__ */ jsxs("div", {
			className: stackClasses({ variant }),
			children: [/* @__PURE__ */ jsx("div", {
				className: contentClasses({ variant }),
				title: variant === "user" ? createdAt : void 0,
				children
			}), actions]
		})
	});
};
//#endregion
//#region src/blocks/AIKit/PlanApproval.tsx
const defaultStepCountLabel = (count) => `${count} step${count === 1 ? "" : "s"}`;
const StatusPill = ({ status, pendingLabel, approvedLabel, dismissedLabel, changesRequestedLabel }) => {
	if (status === "approved") return /* @__PURE__ */ jsx(Badge, {
		color: "green",
		variant: "pill",
		className: "tw:normal-case tw:tracking-normal",
		children: approvedLabel
	});
	if (status === "dismissed") return /* @__PURE__ */ jsx(Badge, {
		color: "gray",
		variant: "pill",
		className: "tw:normal-case tw:tracking-normal",
		children: dismissedLabel
	});
	if (status === "changes-requested") return /* @__PURE__ */ jsx(Badge, {
		color: "blue",
		variant: "pill",
		className: "tw:normal-case tw:tracking-normal",
		children: changesRequestedLabel
	});
	return /* @__PURE__ */ jsx(Badge, {
		color: "green",
		variant: "pill",
		className: "tw:normal-case tw:tracking-normal",
		children: pendingLabel
	});
};
const ExpandToggle = ({ isExpanded, onToggle, showMoreLabel, showLessLabel }) => /* @__PURE__ */ jsxs("button", {
	type: "button",
	onClick: onToggle,
	"aria-expanded": isExpanded,
	className: "tw:inline-flex tw:cursor-pointer tw:items-center tw:gap-1 tw:bg-transparent tw:p-0 tw:text-sm tw:font-medium tw:text-secondary-500 tw:hover:text-secondary-600 tw:focus-visible:outline tw:focus-visible:outline-2 tw:focus-visible:outline-offset-2 tw:focus-visible:outline-ring tailwindcss-preflight",
	children: [/* @__PURE__ */ jsx("span", { children: isExpanded ? showLessLabel : showMoreLabel }), /* @__PURE__ */ jsx(ChevronDown, { className: cn("tw:size-5 tw:transition-transform", isExpanded && "tw:rotate-180") })]
});
const ChangeRequestForm = ({ value, onValueChange, onSubmit, onKeyDown, placeholder, submitLabel, formLabel, canSubmit, isSubmitting }) => /* @__PURE__ */ jsx("form", {
	onSubmit,
	className: "tw:border-0 tw:border-t tw:border-solid tw:border-border tw:bg-card tw:text-card-foreground tw:px-4 tw:py-3",
	"aria-label": formLabel,
	children: /* @__PURE__ */ jsxs("div", {
		className: "tw:relative",
		children: [/* @__PURE__ */ jsx(Textarea, {
			value,
			onValueChange,
			placeholder,
			maxRows: 4,
			autoFocus: true,
			onKeyDown,
			containerClassName: "tw:pr-12",
			disabled: isSubmitting,
			"aria-label": placeholder,
			"data-testid": "plan-change-request-input"
		}), /* @__PURE__ */ jsx(Button, {
			type: "submit",
			variant: "invisible",
			size: "icon-sm",
			"aria-label": submitLabel,
			disabled: !canSubmit,
			className: "tw:absolute tw:right-2 tw:top-1/2 tw:-translate-y-1/2 tw:[&_svg]:text-secondary-500 tw:not-disabled:hover:[&_svg]:text-secondary-600",
			"data-testid": "plan-change-request-submit",
			children: /* @__PURE__ */ jsx(Send, { className: "tw:size-4" })
		})]
	})
});
const PlanActions = ({ isRequestingChanges, isSubmitting, onApprove, onRequestChanges, onOpenRequestChanges, onCancelChangeRequest, approveLabel, requestChangesLabel, cancelLabel, approveTooltip }) => /* @__PURE__ */ jsxs("div", {
	className: "tw:flex tw:shrink-0 tw:items-center tw:gap-2",
	children: [isRequestingChanges ? /* @__PURE__ */ jsx(Button, {
		variant: "outline",
		size: "small",
		onClick: onCancelChangeRequest,
		disabled: isSubmitting,
		"data-testid": "plan-cancel-button",
		className: "tw:shrink-0 tw:whitespace-nowrap",
		children: cancelLabel
	}) : onRequestChanges && /* @__PURE__ */ jsx(Button, {
		variant: "outline",
		size: "small",
		onClick: onOpenRequestChanges,
		disabled: isSubmitting,
		"data-testid": "plan-request-changes-button",
		className: "tw:shrink-0 tw:whitespace-nowrap",
		children: requestChangesLabel
	}), onApprove && (approveTooltip ? /* @__PURE__ */ jsx(Tooltip, {
		tooltip: approveTooltip,
		placement: "top",
		triggerAsChild: true,
		children: /* @__PURE__ */ jsx(Button, {
			variant: "primary",
			size: "small",
			onClick: onApprove,
			disabled: isSubmitting || isRequestingChanges,
			"data-testid": "plan-approve-button",
			className: "tw:shrink-0 tw:whitespace-nowrap",
			children: approveLabel
		})
	}) : /* @__PURE__ */ jsx(Button, {
		variant: "primary",
		size: "small",
		onClick: onApprove,
		disabled: isSubmitting || isRequestingChanges,
		"data-testid": "plan-approve-button",
		className: "tw:shrink-0 tw:whitespace-nowrap",
		children: approveLabel
	}))]
});
const PlanApproval = ({ status, title, summary, stepCount, planBody, defaultExpanded = false, isExpanded: isExpandedProp, onExpandToggle, onApprove, onRequestChanges, onClose, isSubmitting = false, approveTooltip, pendingPillLabel = "Ready for approval", approvedPillLabel = "Approved", dismissedPillLabel = "Dismissed", changesRequestedPillLabel = "Changes Requested", approveLabel = "Approve", requestChangesLabel = "Request Changes", cancelLabel = "Cancel", showMoreLabel = "Show more", showLessLabel = "Show less", closeLabel = "Dismiss plan", fullScreenLabel = "View full screen", exitFullScreenLabel = "Exit full screen", changeRequestPlaceholder = "What would you like to change in the plan?", changeRequestSubmitLabel = "Send change request", stepCountLabel = defaultStepCountLabel, className }) => {
	const [uncontrolledExpanded, setUncontrolledExpanded] = useState(defaultExpanded);
	const isExpanded = isExpandedProp ?? uncontrolledExpanded;
	const [isRequestingChanges, setIsRequestingChanges] = useState(false);
	const [changeRequest, setChangeRequest] = useState("");
	const [isFullScreen, setIsFullScreen] = useState(false);
	const isPending = status === "pending";
	const showActions = isPending && (onApprove || onRequestChanges);
	const showClose = isPending && Boolean(onClose);
	const canShowPlanBody = planBody != null;
	const trimmedChangeRequest = changeRequest.trim();
	const canSubmitChangeRequest = trimmedChangeRequest.length > 0 && !isSubmitting;
	const [isClosing, setIsClosing] = useState(false);
	const toggleExpanded = () => {
		const next = !isExpanded;
		if (!next) setIsClosing(hasMotion());
		if (isExpandedProp === void 0) setUncontrolledExpanded(next);
		onExpandToggle?.(next);
	};
	const handleOpenRequestChanges = () => {
		setIsRequestingChanges(true);
		if (!isExpanded) toggleExpanded();
	};
	const handleCancelChangeRequest = () => {
		setIsRequestingChanges(false);
		setChangeRequest("");
	};
	const handleSubmitChangeRequest = (event) => {
		event?.preventDefault();
		if (!canSubmitChangeRequest) return;
		onRequestChanges?.(trimmedChangeRequest);
	};
	const handleTextareaKeyDown = (event) => {
		if (event.key === "Enter" && !event.shiftKey) {
			event.preventDefault();
			handleSubmitChangeRequest();
		}
	};
	const showChangeRequestForm = isRequestingChanges && isPending;
	const changeRequestForm = /* @__PURE__ */ jsx(ChangeRequestForm, {
		value: changeRequest,
		onValueChange: setChangeRequest,
		onSubmit: handleSubmitChangeRequest,
		onKeyDown: handleTextareaKeyDown,
		placeholder: changeRequestPlaceholder,
		submitLabel: changeRequestSubmitLabel,
		formLabel: requestChangesLabel,
		canSubmit: canSubmitChangeRequest,
		isSubmitting
	});
	const planActions = /* @__PURE__ */ jsx(PlanActions, {
		isRequestingChanges,
		isSubmitting,
		onApprove,
		onRequestChanges,
		onOpenRequestChanges: handleOpenRequestChanges,
		onCancelChangeRequest: handleCancelChangeRequest,
		approveLabel,
		requestChangesLabel,
		cancelLabel,
		approveTooltip
	});
	return /* @__PURE__ */ jsxs(Fragment$1, { children: [/* @__PURE__ */ jsxs("section", {
		"aria-label": title,
		className: cn("tw:flex tw:w-full tw:max-w-[800px] tw:flex-col tw:overflow-hidden tw:rounded-xl tw:border tw:border-solid tw:border-border tw:bg-background tw:text-foreground", className),
		"data-testid": "plan-approval-root",
		"data-plan-status": status,
		children: [
			/* @__PURE__ */ jsxs("div", {
				className: "tw:flex tw:items-start tw:gap-2 tw:pl-4 tw:pr-2 tw:pt-4",
				children: [/* @__PURE__ */ jsxs("div", {
					className: "tw:flex tw:min-w-0 tw:flex-1 tw:flex-col tw:gap-2 tw:pb-4",
					children: [/* @__PURE__ */ jsxs("div", {
						className: "tw:flex tw:items-center tw:gap-3",
						children: [/* @__PURE__ */ jsx(StatusPill, {
							status,
							pendingLabel: pendingPillLabel,
							approvedLabel: approvedPillLabel,
							dismissedLabel: dismissedPillLabel,
							changesRequestedLabel: changesRequestedPillLabel
						}), stepCount != null && stepCount > 0 && /* @__PURE__ */ jsx("span", {
							className: "tw:text-xs tw:leading-4 tw:text-muted-foreground",
							children: stepCountLabel(stepCount)
						})]
					}), /* @__PURE__ */ jsx("p", {
						className: "tw:m-0 tw:text-base tw:font-semibold tw:leading-6",
						children: title
					})]
				}), /* @__PURE__ */ jsxs("div", {
					className: "tw:flex tw:items-center tw:gap-2",
					children: [canShowPlanBody && !isFullScreen && /* @__PURE__ */ jsx(Tooltip, {
						tooltip: fullScreenLabel,
						placement: "top",
						triggerAsChild: true,
						children: /* @__PURE__ */ jsx(Button, {
							variant: "outline",
							size: "icon-sm",
							"aria-label": fullScreenLabel,
							onClick: () => setIsFullScreen(true),
							"data-testid": "plan-fullscreen-button",
							children: /* @__PURE__ */ jsx(Maximize2, { className: "tw:size-4" })
						})
					}), showClose && !isFullScreen && /* @__PURE__ */ jsx(Button, {
						variant: "outline",
						size: "icon-sm",
						"aria-label": closeLabel,
						onClick: onClose,
						disabled: isSubmitting,
						"data-testid": "plan-close-button",
						children: /* @__PURE__ */ jsx(X, { className: "tw:size-4" })
					})]
				})]
			}),
			summary != null && /* @__PURE__ */ jsx("div", {
				className: cn("tw:whitespace-pre-wrap tw:border-0 tw:border-t tw:border-solid tw:border-border tw:px-4 tw:pt-4 tw:text-sm", isExpanded && canShowPlanBody ? "tw:pb-4" : "tw:pb-2"),
				children: summary
			}),
			canShowPlanBody && /* @__PURE__ */ jsx(CollapsibleBody, {
				isExpanded,
				isClosing,
				onClosed: () => setIsClosing(false),
				children: /* @__PURE__ */ jsx("div", {
					className: "tw:max-h-[400px] tw:overflow-y-auto tw:border-0 tw:border-t tw:border-solid tw:border-border tw:bg-card tw:p-4 tw:text-sm tw:text-card-foreground",
					children: planBody
				})
			}),
			!isFullScreen && /* @__PURE__ */ jsxs(Fragment$1, { children: [showChangeRequestForm && changeRequestForm, /* @__PURE__ */ jsxs("div", {
				className: cn("tw:flex tw:items-center tw:gap-2 tw:overflow-x-auto tw:border-0 tw:border-t tw:border-solid tw:border-border tw:bg-card tw:px-4 tw:py-3 tw:text-card-foreground", canShowPlanBody ? "tw:justify-between" : "tw:justify-end"),
				children: [canShowPlanBody && /* @__PURE__ */ jsx("div", {
					className: "tw:shrink-0",
					children: /* @__PURE__ */ jsx(ExpandToggle, {
						isExpanded,
						onToggle: toggleExpanded,
						showMoreLabel,
						showLessLabel
					})
				}), showActions && planActions]
			})] })
		]
	}), canShowPlanBody && isFullScreen && /* @__PURE__ */ jsxs(FullScreenModal, {
		onEscapeKeyDown: () => setIsFullScreen(false),
		children: [
			/* @__PURE__ */ jsx(FullScreenModalHeader, {
				title: /* @__PURE__ */ jsxs("div", {
					className: "tw:flex tw:min-w-0 tw:items-center tw:gap-3",
					children: [/* @__PURE__ */ jsx(StatusPill, {
						status,
						pendingLabel: pendingPillLabel,
						approvedLabel: approvedPillLabel,
						dismissedLabel: dismissedPillLabel,
						changesRequestedLabel: changesRequestedPillLabel
					}), /* @__PURE__ */ jsx("span", {
						className: "tw:truncate",
						children: title
					})]
				}),
				onClose: () => setIsFullScreen(false),
				showPreviousTitle: false,
				children: /* @__PURE__ */ jsxs("div", {
					className: "tw:flex tw:items-center tw:gap-2",
					children: [showClose && /* @__PURE__ */ jsx(Tooltip, {
						tooltip: closeLabel,
						placement: "bottom",
						triggerAsChild: true,
						children: /* @__PURE__ */ jsx(Button, {
							variant: "outline",
							size: "icon",
							"aria-label": closeLabel,
							onClick: onClose,
							disabled: isSubmitting,
							"data-testid": "plan-close-button",
							children: /* @__PURE__ */ jsx(X, { className: "tw:size-4" })
						})
					}), /* @__PURE__ */ jsx(Tooltip, {
						tooltip: exitFullScreenLabel,
						placement: "bottom",
						triggerAsChild: true,
						children: /* @__PURE__ */ jsx(Button, {
							variant: "outline",
							size: "icon",
							"aria-label": exitFullScreenLabel,
							onClick: () => setIsFullScreen(false),
							"data-testid": "plan-exit-fullscreen-button",
							children: /* @__PURE__ */ jsx(Minimize2, { className: "tw:size-4" })
						})
					})]
				})
			}),
			/* @__PURE__ */ jsx(FullScreenModalBody, {
				className: "tw:bg-card tw:px-6 tw:py-4 tw:text-card-foreground",
				children: /* @__PURE__ */ jsx("div", {
					className: "tw:mx-auto tw:w-full tw:max-w-[800px] tw:text-sm",
					children: planBody
				})
			}),
			(showChangeRequestForm || showActions) && /* @__PURE__ */ jsx("div", {
				className: "tw:shrink-0 tw:border-0 tw:border-t tw:border-solid tw:border-border tw:bg-card tw:text-card-foreground",
				children: /* @__PURE__ */ jsxs("div", {
					className: "tw:mx-auto tw:w-full tw:max-w-[800px]",
					children: [showChangeRequestForm && changeRequestForm, showActions && /* @__PURE__ */ jsx("div", {
						className: "tw:flex tw:justify-end tw:px-4 tw:py-3",
						children: planActions
					})]
				})
			})
		]
	})] });
};
//#endregion
//#region src/blocks/AIKit/PlanRewritingIndicator.tsx
const PlanRewritingIndicator = ({ label = "Rewriting the plan..." }) => /* @__PURE__ */ jsxs("div", {
	className: "tw:flex tw:items-center tw:gap-2",
	role: "status",
	"data-testid": "plan-rewriting-indicator",
	children: [/* @__PURE__ */ jsx(Loader2, { className: "tw:size-4 tw:animate-spin tw:text-secondary-500" }), /* @__PURE__ */ jsx("span", {
		className: "tw:text-sm tw:italic tw:text-secondary-500",
		children: label
	})]
});
//#endregion
//#region src/blocks/AIKit/constants.ts
/**
* Names the container query `PromptInput`'s `inline` row reads to fold into the stacked layout.
*
* It has to sit on an **ancestor**: a container query matches descendants only, so the field that
* would read it cannot name it, and its own wrapper is the width the fold changes. A host that
* never names it keeps the one-line row at every width.
*/
const PROMPT_INPUT_CONTAINER = "tw:@container/composer";
//#endregion
//#region src/blocks/AIKit/AnimatedHeight.tsx
/**
* A box whose height eases to whatever its content measures.
*
* - Content is measured with a `ResizeObserver`; without one (jsdom) the box is plain `auto`.
* - The first measurement lands without a transition, so mounting with content does not grow in.
* - Overflow is clipped only while the height moves, so focus rings are not cut off at rest.
* - `prefers-reduced-motion` drops the transition.
*/
const AnimatedHeight = ({ children, className }) => {
	const contentRef = useRef(null);
	const heightRef = useRef(void 0);
	const [height, setHeight] = useState();
	const [isAnimating, setIsAnimating] = useState(false);
	useLayoutEffect(() => {
		const node = contentRef.current;
		if (!node || typeof ResizeObserver === "undefined") return;
		const observer = new ResizeObserver(() => {
			const next = node.offsetHeight;
			const prev = heightRef.current;
			if (prev !== void 0 && prev !== next && hasMotion()) setIsAnimating(true);
			heightRef.current = next;
			setHeight(next);
		});
		observer.observe(node);
		return () => observer.disconnect();
	}, []);
	const handleTransitionEnd = (event) => {
		if (event.target !== event.currentTarget || event.propertyName !== "height") return;
		setIsAnimating(false);
	};
	return /* @__PURE__ */ jsx("div", {
		style: { height },
		onTransitionEnd: handleTransitionEnd,
		className: cn("tw:transition-[height] tw:duration-200 tw:ease-out tw:motion-reduce:transition-none", isAnimating && "tw:overflow-hidden", className),
		children: /* @__PURE__ */ jsx("div", {
			ref: contentRef,
			children
		})
	});
};
//#endregion
//#region src/blocks/AIKit/PromptInputAction.tsx
const PromptInputAction = ({ ref, onMouseDown, ...rest }) => {
	const handleOnMouseDown = (event) => {
		event.stopPropagation();
		onMouseDown?.(event);
	};
	return /* @__PURE__ */ jsx(Slot, {
		...rest,
		ref,
		onMouseDown: handleOnMouseDown
	});
};
//#endregion
//#region src/blocks/AIKit/PromptInputCommands.tsx
/** What the menu needs above or below the composer before it will open on that side. */
const MENU_HEIGHT = 440;
/** As tall as the list ever gets, however much room there is. */
const MENU_MAX_HEIGHT = 416;
/** Space kept from the measured edge; `POPUP_CHROME` is the popup's own border and padding. */
const MENU_GUTTER = 16;
const POPUP_CHROME = 10;
/**
* The rows a typed filter leaves.
*
* - An empty filter keeps everything.
* - A row matches on its label or its value.
* - Matching nothing falls back to the action rows.
*/
const matchesFilter = (items, filterValue) => {
	if (!filterValue) return items;
	const query = filterValue.toLowerCase();
	const matched = items.filter((item) => item.label.toLowerCase().includes(query) || item.value.toLowerCase().includes(query));
	return matched.length > 0 ? matched : items.filter((item) => item.onSelect !== void 0);
};
/**
* The band the menu has to stay inside: the window, narrowed by every ancestor that clips.
*
* - The chat sidebar's panel clips well below the window's top edge.
*/
const CLIPS = /* @__PURE__ */ new Set([
	"hidden",
	"auto",
	"scroll",
	"clip",
	"overlay"
]);
const clipBand = (node) => {
	let top = 0;
	let bottom = window.innerHeight;
	let parent = node.parentElement;
	while (parent) {
		const { overflowY, overflowX } = window.getComputedStyle(parent);
		if (CLIPS.has(overflowY) || CLIPS.has(overflowX)) {
			const box = parent.getBoundingClientRect();
			top = Math.max(top, box.top);
			bottom = Math.min(bottom, box.bottom);
		}
		parent = parent.parentElement;
	}
	return {
		top,
		bottom
	};
};
const PromptInputCommands = ({ items, open, onSelect, filterValue, emptyText = "No commands found", panel, groupAction, groupHeading, children }) => {
	const filtered = matchesFilter(items, filterValue);
	const [highlighted, setHighlighted] = useState();
	const [wasOpen, setWasOpen] = useState(open);
	if (open !== wasOpen) {
		setWasOpen(open);
		if (open) setHighlighted(void 0);
	}
	/**
	* Which side the menu opens on.
	*
	* - Above by default; below when there is not room above and more room below.
	* - Re-measured while open: the composer and the transcript above it move.
	*/
	const rootRef = useRef(null);
	const [below, setBelow] = useState(false);
	const [maxHeight, setMaxHeight] = useState(MENU_MAX_HEIGHT);
	const measure = useCallback(() => {
		const node = rootRef.current;
		const box = node?.getBoundingClientRect();
		if (!node || !box) return;
		const band = clipBand(node);
		const above = box.top - band.top;
		const under = band.bottom - box.bottom;
		const openBelow = above < MENU_HEIGHT && under > above;
		setBelow(openBelow);
		setMaxHeight(Math.max(0, Math.min(MENU_MAX_HEIGHT, (openBelow ? under : above) - MENU_GUTTER - POPUP_CHROME)));
	}, []);
	const attachRoot = useCallback((node) => {
		rootRef.current = node;
		if (node) measure();
	}, [measure]);
	useEffect(() => {
		if (!open) return;
		const node = rootRef.current;
		const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
		if (node) observer?.observe(node);
		window.addEventListener("resize", measure);
		window.addEventListener("scroll", measure, true);
		return () => {
			observer?.disconnect();
			window.removeEventListener("resize", measure);
			window.removeEventListener("scroll", measure, true);
		};
	}, [open, measure]);
	const highlightedValue = filtered.some((item) => item.value === highlighted) ? highlighted : filtered[0]?.value;
	const groups = filtered.reduce((acc, item) => {
		const group = acc[item.group] ?? [];
		group.push(item);
		acc[item.group] = group;
		return acc;
	}, {});
	const menuStyle = { "--prompt-menu-max-h": `${maxHeight}px` };
	return /* @__PURE__ */ jsxs(Command, {
		ref: attachRoot,
		loop: true,
		className: "tw:relative",
		shouldFilter: false,
		value: highlightedValue ?? "",
		onValueChange: setHighlighted,
		children: [open && /* @__PURE__ */ jsx("div", {
			className: cn("tw:absolute tw:left-0 tw:z-50 tw:w-full tw:max-w-lg", below ? "tw:top-full tw:mt-1" : "tw:bottom-full tw:mb-1", "tw:rounded-[14px] tw:border tw:border-border tw:bg-popover tw:text-popover-foreground tw:shadow-lg", "tw:p-1"),
			style: menuStyle,
			children: panel ? /* @__PURE__ */ jsx("div", {
				className: "tw:p-3",
				children: panel
			}) : filtered.length === 0 ? /* @__PURE__ */ jsx("div", {
				className: "tw:py-3 tw:pl-4 tw:text-center tw:text-sm tw:text-muted-foreground",
				children: emptyText
			}) : /* @__PURE__ */ jsx(Command.List, {
				className: "tw:group-data-[variant=argo]/command:px-1 tw:group-data-[variant=argo]/command:pb-1",
				viewPortClassName: "tw:max-h-[var(--prompt-menu-max-h)] tw:[&>div]:!block",
				children: Object.entries(groups).map(([groupName, groupItems], index) => /* @__PURE__ */ jsx(Command.Group, {
					heading: groupName ? /* @__PURE__ */ jsxs("span", {
						className: "tw:flex tw:items-center tw:justify-between tw:gap-2",
						children: [/* @__PURE__ */ jsx("span", {
							className: "tw:min-w-0 tw:truncate",
							children: groupHeading?.(groupName) ?? groupName
						}), groupAction?.(groupName)]
					}) : void 0,
					className: cn("tw:[&_[cmdk-group-heading]]:normal-case", "tw:[&_[cmdk-group-heading]]:tracking-normal", "tw:[&_[cmdk-group-heading]]:border-b-0", "tw:[&_[cmdk-group-heading]]:!py-0", "tw:[&_[cmdk-group-heading]]:mt-2", "tw:[&_[cmdk-group-heading]]:mb-1", "tw:[&_[cmdk-group-heading]]:pl-2", "tw:[&_[cmdk-group-heading]]:text-xs", index > 0 && "tw:mt-3"),
					children: groupItems.map((item) => /* @__PURE__ */ jsxs(Command.Item, {
						value: item.value,
						className: "tw:w-full tw:min-w-0 tw:rounded-md",
						onSelect: () => onSelect(item),
						children: [item.icon, /* @__PURE__ */ jsxs("div", {
							className: "tw:flex tw:min-w-0 tw:flex-1 tw:flex-col",
							children: [item.content ?? /* @__PURE__ */ jsx("span", {
								className: "tw:truncate",
								children: item.label
							}), item.description && /* @__PURE__ */ jsx(Truncated, {
								text: item.description,
								twoLines: true,
								className: "tw:text-xs tw:text-muted-foreground"
							})]
						})]
					}, item.value))
				}, groupName))
			})
		}), children]
	});
};
//#endregion
//#region src/blocks/AIKit/PromptInputMenuTrigger.tsx
/**
* The composer's "add to message" button.
*
* - One glyph for both states: a plus rotated 45° reads as a close cross, so nothing swaps.
* - `aria-expanded` mirrors `open`; the accessible name changes with it.
* - Wrapped in `PromptInputAction` so a click does not hand focus to the textarea.
*/
const PromptInputMenuTrigger = ({ open = false, large = false, className, ref, ...props }) => /* @__PURE__ */ jsx(PromptInputAction, { children: /* @__PURE__ */ jsx(Button, {
	ref,
	type: "button",
	size: large ? "icon" : "icon-sm",
	variant: "invisible",
	"aria-expanded": open,
	"aria-label": open ? "Close menu" : "Add to message",
	"data-testid": "prompt-input-menu-trigger",
	className: cn("tw:rounded-full", "tw:hover:not-disabled:bg-muted tw:hover:not-disabled:text-foreground tw:hover:not-disabled:border-transparent", "tw:aria-expanded:bg-muted", className),
	...props,
	children: /* @__PURE__ */ jsx(Plus, { className: cn(large ? "tw:size-5" : "tw:size-4", "tw:transition-transform tw:duration-300 tw:ease-out tw:motion-reduce:transition-none", open && "tw:rotate-135") })
}) });
//#endregion
//#region src/blocks/AIKit/PromptInputSubmit.tsx
const ARIA_LABEL_BY_STATUS = {
	ready: "Send message",
	streaming: "Stop",
	interrupted: "Continue",
	submitted: "Sending…"
};
const PromptInputSubmit = ({ ref, disabled, status = "ready" }) => {
	const icon = (() => {
		if (status === "streaming") return /* @__PURE__ */ jsx(CirclePause, { className: "tw:size-4" });
		if (status === "interrupted") return /* @__PURE__ */ jsx(Play, { className: "tw:size-4" });
		return /* @__PURE__ */ jsx(Send, { className: "tw:size-4 tw:translate-x-[-1px] tw:translate-y-[1px]" });
	})();
	const getTooltipText = () => {
		if (status === "streaming") return "Stop";
		if (status === "interrupted") return "Continue";
	};
	return /* @__PURE__ */ jsxs(Tooltip.Root, { children: [/* @__PURE__ */ jsx(Tooltip.Trigger, {
		asChild: true,
		children: /* @__PURE__ */ jsx(PromptInputAction, { children: /* @__PURE__ */ jsx(Button, {
			ref,
			type: "submit",
			disabled,
			"aria-label": ARIA_LABEL_BY_STATUS[status],
			size: "icon-sm",
			variant: "primary",
			isLoading: status === "submitted",
			className: cn("tw:ml-auto tw:flex tw:items-center tw:rounded-full", "tw:bg-gradient-to-br tw:bg-origin-border tw:from-[#61E4FF] tw:via-[#1F8FFF] tw:to-[#7C5CFF]", "tw:hover:not-disabled:brightness-110"),
			children: status !== "submitted" && icon
		}) })
	}), getTooltipText() && /* @__PURE__ */ jsx(Tooltip.Content, {
		placement: "top",
		variant: "action",
		children: getTooltipText()
	})] });
};
//#endregion
//#region src/blocks/AIKit/PromptInput.tsx
const CMDK_HIJACKED_KEYS = /* @__PURE__ */ new Set([
	"ArrowUp",
	"ArrowDown",
	"Home",
	"End",
	"Enter"
]);
/**
* Whether the composer's always-mounted cmdk root would act on this keystroke, cancelling its default.
*
* - cmdk acts on every `CMDK_HIJACKED_KEYS` key regardless of modifiers, except a modified Enter.
* - The textarea and the form stop these from bubbling, or a focused button never clicks on Enter.
*/
const isCmdkNavigation = (event) => CMDK_HIJACKED_KEYS.has(event.key) && (event.key !== "Enter" || !(event.metaKey || event.ctrlKey || event.altKey));
/**
* The `@token` the caret sits at the end of, if any.
*
* - `@` has to open a word: it must start the value or follow whitespace.
* - The token holds no whitespace or second `@` and runs past the caret, so a mid-word pick takes the word.
* - `start` indexes the `@` itself, so replacing `[start, end)` removes the token.
*/
const findMentionToken = (value, caret) => {
	const before = value.slice(0, caret);
	const match = /(?:^|\s)@([^\s@]*)$/.exec(before);
	if (!match) return null;
	const typed = match[1] ?? "";
	const tail = /^[^\s@]*/.exec(value.slice(caret))?.[0] ?? "";
	return {
		start: caret - typed.length - 1,
		end: caret + tail.length,
		query: typed + tail
	};
};
/**
* The `/token` a message leads with, while the caret is still on it.
*
* - Only the first token: the Agent SDK expands a skill only at the start of a message.
* - Text may follow it.
* - The caret past the token closes the menu.
*/
const findSlashToken = (value, caret) => {
	const match = /^\/(\S*)/.exec(value);
	if (!match) return null;
	const end = match[0].length;
	if (caret > end) return null;
	return {
		end,
		query: match[1] ?? ""
	};
};
const promptInputVariants = cva([
	"tailwindcss-preflight",
	"tw:relative",
	"tw:flex",
	"tw:gap-2",
	"tw:justify-center",
	"tw:border",
	"tw:border-2",
	"tw:border-transparent",
	"tw:rounded-[14px]",
	"tw:bg-gradient-to-bl",
	"tw:from-[#61E4FF]",
	"tw:via-[#1F8FFF]",
	"tw:to-[#7C5CFF]",
	"tw:bg-origin-border",
	"tw:shadow-[inset_0_100vw_rgb(var(--card))]",
	"tw:has-[textarea:focus]:ring-4",
	"tw:has-[textarea:focus]:ring-ring/30",
	"tw:cursor-text",
	"tw:px-2"
], {
	defaultVariants: { variant: "default" },
	variants: { variant: {
		default: [
			"tw:flex-col",
			"tw:pt-3",
			"tw:pb-2"
		],
		inline: ["tw:flex-col", "tw:py-3"]
	} }
});
const wrapperVariants = cva(null, {
	defaultVariants: { variant: "default" },
	variants: { variant: {
		default: "tw:rounded-[14px]",
		inline: ""
	} }
});
const PromptInput = ({ onSend, onAbort, onContinue, prefix, variant, suffix, attachments, className, disabled, disableSubmit, value = "", onValueChange, status = "ready", maxRows = 6, minRows, commands, commandsGroupAction, panel, mentions, onMentionSelect, mentionsEmptyText = "Nothing to attach", mentionsGroupHeading, mentionsMultiple = false, autoFocus, textareaRef: publishedRef, ...textareaProps }) => {
	const defaultMinRows = variant === "inline" ? 1 : 2;
	const isDisabled = (disabled || disableSubmit || !value.trim()) && status !== "streaming" && status !== "interrupted";
	const textareaId = useId();
	const textareaRef = useRef(null);
	const setTextareaNode = useCallback((node) => {
		textareaRef.current = node;
		if (publishedRef) publishedRef.current = node;
	}, [publishedRef]);
	const [dismissedValue, setDismissedValue] = useState(null);
	const [caret, setCaret] = useState(0);
	const [caretValue, setCaretValue] = useState(value);
	const moveCaret = (next, forValue) => {
		setCaret(next);
		setCaretValue(forValue);
	};
	if (value !== caretValue) moveCaret(value.length, value);
	const autoFocusRef = useRef(autoFocus);
	useEffect(() => {
		if (autoFocusRef.current) textareaRef.current?.focus({ preventScroll: true });
	}, []);
	if (dismissedValue !== null && value !== dismissedValue) setDismissedValue(null);
	const hasCommands = !!commands && commands.length > 0;
	const panelOpen = panel !== void 0;
	const hasMentions = mentions !== void 0;
	const hasMenus = hasCommands || hasMentions;
	const slashToken = hasCommands && !panelOpen && value !== dismissedValue ? findSlashToken(value, Math.min(caret, value.length)) : null;
	const commandsOpen = slashToken !== null;
	const mentionToken = hasMentions && !commandsOpen && !panelOpen && value !== dismissedValue ? findMentionToken(value, Math.min(caret, value.length)) : null;
	const mentionsOpen = mentionToken !== null;
	const menuOpen = commandsOpen || mentionsOpen || panelOpen;
	const menuItems = commandsOpen ? commands ?? [] : mentions ?? [];
	const menuFilter = commandsOpen ? slashToken.query : mentionToken?.query ?? "";
	const handleSubmit = () => {
		if (isDisabled) return;
		if (status === "streaming") onAbort?.();
		if (status === "interrupted") onContinue?.();
		if (status === "ready") onSend?.(value);
	};
	const handleOnSubmit = (event) => {
		event.preventDefault();
		handleSubmit();
	};
	const handleOnChange = (event) => {
		moveCaret(event.target.selectionStart ?? event.target.value.length, event.target.value);
		onValueChange?.(event.target.value);
	};
	const handleOnSelect = (event) => {
		moveCaret(event.currentTarget.selectionStart ?? 0, event.currentTarget.value);
	};
	const handleMenuSelect = (item) => {
		if (item.onSelect) {
			const start = mentionToken?.start ?? 0;
			const end = mentionToken?.end ?? slashToken?.end;
			if (end !== void 0) {
				const next = value.slice(0, start) + value.slice(end);
				onValueChange?.(next);
				moveCaret(start, next);
			}
			item.onSelect();
			textareaRef.current?.focus();
			return;
		}
		if (mentionToken) {
			const keep = mentionsMultiple ? "@" : "";
			const next = value.slice(0, mentionToken.start) + keep + value.slice(mentionToken.end);
			const nextCaret = mentionToken.start + keep.length;
			onValueChange?.(next);
			moveCaret(nextCaret, next);
			requestAnimationFrame(() => {
				textareaRef.current?.setSelectionRange(nextCaret, nextCaret);
			});
			onMentionSelect?.(item);
		} else {
			const rest = slashToken ? value.slice(slashToken.end).replace(/^\s+/, "") : "";
			const head = `${item.value} `;
			onValueChange?.(head + rest);
			moveCaret(head.length, head + rest);
			requestAnimationFrame(() => {
				textareaRef.current?.setSelectionRange(head.length, head.length);
			});
		}
		textareaRef.current?.focus();
	};
	const dismissMenu = () => {
		if (value.trim() === "/" || value.trim() === "@") {
			onValueChange?.("");
			return;
		}
		if (mentionToken && mentionToken.query === "") {
			const next = (value.slice(0, mentionToken.start) + value.slice(mentionToken.end)).replace(/\s+$/, "");
			onValueChange?.(next);
			moveCaret(next.length, next);
			return;
		}
		setDismissedValue(value);
	};
	const handleOnKeyDown = (event) => {
		if (menuOpen) {
			if (event.key === "Escape") {
				event.preventDefault();
				if (panel) panel.onClose();
				else dismissMenu();
				return;
			}
			if (panelOpen) return;
			if (event.key === "ArrowUp" || event.key === "ArrowDown") return;
			if (event.key === "Enter" && matchesFilter(menuItems, menuFilter).length > 0) return;
		} else if (hasMenus && isCmdkNavigation(event)) event.stopPropagation();
		if (event.key === "Enter" && !event.shiftKey && !event.ctrlKey && !event.metaKey) {
			event.preventDefault();
			if (status !== "streaming") handleSubmit();
		}
	};
	const handleOnFormKeyDown = (event) => {
		if (hasMenus && event.target !== textareaRef.current && isCmdkNavigation(event)) event.stopPropagation();
	};
	const handleOnMouseDown = (event) => {
		const { currentTarget, target } = event;
		if (target === textareaRef.current) return;
		if (!(target instanceof Node && currentTarget.contains(target))) return;
		event.preventDefault();
		textareaRef.current?.focus();
	};
	const formEl = /* @__PURE__ */ jsx("div", {
		className: wrapperVariants({ variant }),
		children: /* @__PURE__ */ jsxs("form", {
			"data-testid": "prompt-input",
			"data-status": status,
			onKeyDown: handleOnFormKeyDown,
			onMouseDown: handleOnMouseDown,
			onSubmit: handleOnSubmit,
			className: cn(promptInputVariants({ variant }), disabled && "tw:bg-none tw:border-border tw:shadow-[inset_0_100vw_rgb(var(--muted))] tw:cursor-default", className),
			children: [
				/* @__PURE__ */ jsx(AnimatedHeight, {
					className: "tw:-mb-2",
					children: attachments && /* @__PURE__ */ jsx("div", {
						className: "tw:flex tw:flex-col tw:gap-2 tw:pb-4",
						children: attachments
					})
				}),
				/* @__PURE__ */ jsx("label", {
					htmlFor: textareaId,
					className: "tw:sr-only",
					children: "Prompt Input"
				}),
				/* @__PURE__ */ jsxs("div", {
					className: cn("tw:flex tw:min-w-0 tw:gap-2", variant === "inline" ? "tw:flex-row tw:items-center tw:@max-[480px]/composer:flex-wrap" : "tw:flex-col"),
					children: [
						prefix && /* @__PURE__ */ jsx("div", {
							className: "tw:flex tw:shrink-0 tw:items-center",
							children: prefix
						}),
						/* @__PURE__ */ jsx("div", {
							className: cn("tw:flex tw:min-w-0 tw:items-center", variant === "inline" ? "tw:grow tw:@max-[480px]/composer:order-first tw:@max-[480px]/composer:basis-full" : "tw:flex-1"),
							children: /* @__PURE__ */ jsx(TextareaAutosize, {
								...textareaProps,
								disabled,
								minRows: minRows ?? defaultMinRows,
								maxRows,
								id: textareaId,
								ref: setTextareaNode,
								value,
								onChange: handleOnChange,
								onSelect: handleOnSelect,
								onKeyDown: handleOnKeyDown,
								className: cn("tw:resize-none", "tw:outline-hidden", "tw:min-w-32 tw:flex-1", variant === "inline" ? "tw:@max-[480px]/composer:pl-2" : "tw:pl-2 tw:pr-0", "tw:bg-transparent", disabled ? "tw:text-muted-foreground" : "tw:text-foreground", "tw:text-sm", "tw:font-sans", "tw:transition-[width]", "tw:delay-75", "tw:ease-linear", "tw:overflow-auto")
							})
						}),
						/* @__PURE__ */ jsxs("div", {
							className: cn("tw:flex tw:items-center tw:gap-1", "tw:[&>*:last-child]:ml-1", "tw:shrink-0", variant === "inline" && "tw:pr-2.5 tw:@max-[480px]/composer:grow tw:@max-[480px]/composer:pr-0"),
							children: [suffix, /* @__PURE__ */ jsx(PromptInputSubmit, {
								disabled: isDisabled,
								status
							})]
						})
					]
				})
			]
		})
	});
	return /* @__PURE__ */ jsx(TooltipProvider, { children: hasMenus ? /* @__PURE__ */ jsx(PromptInputCommands, {
		items: menuItems,
		open: menuOpen,
		onSelect: handleMenuSelect,
		filterValue: menuFilter,
		emptyText: mentionsOpen ? mentionsEmptyText : "No commands found",
		panel: panel?.content,
		groupAction: commandsOpen ? commandsGroupAction : void 0,
		groupHeading: mentionsOpen ? mentionsGroupHeading : void 0,
		children: formEl
	}) : formEl });
};
PromptInput.Action = PromptInputAction;
PromptInput.MenuTrigger = PromptInputMenuTrigger;
//#endregion
//#region src/blocks/AIKit/Question.tsx
const isEditableTarget = (target) => target instanceof HTMLElement && (target.isContentEditable || [
	"INPUT",
	"TEXTAREA",
	"SELECT"
].includes(target.tagName));
const isFieldWithText = (element) => {
	if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) return element.value !== "";
	return element instanceof HTMLElement && element.isContentEditable && !!element.textContent;
};
const hasCommandModifier = (e) => e.altKey || e.ctrlKey || e.metaKey;
const SEND_KEY_CLASS = "tw:!bg-white/20 tw:!text-primary-foreground";
const keyRange = (count) => count > 1 ? `1–${count}` : "1";
/** An option's key, or a blank slot past the ninth; a picked option's turns into a check. */
const OptionKey = ({ digit, checked }) => digit === null && !checked ? /* @__PURE__ */ jsx("span", {
	"aria-hidden": true,
	className: "tw:w-5 tw:shrink-0"
}) : /* @__PURE__ */ jsx(Kbd, {
	"aria-hidden": true,
	className: cn("tw:-my-0.5", checked && "tw:!bg-primary tw:!text-primary-foreground"),
	"data-testid": "question-option-digit",
	children: checked ? /* @__PURE__ */ jsx(Check, { className: "tw:size-3 tw:animate-in tw:fade-in tw:zoom-in-50 tw:duration-150" }) : digit
});
const handleKeyDown = (callback) => (e) => {
	if (hasCommandModifier(e) || e.repeat) return;
	if (e.key === "Enter" || e.key === " ") {
		e.preventDefault();
		callback();
	}
};
const Question = ({ question, header, options, multiSelect = true, selectedOptionIds = [], onSelectionChange, customAnswer = "", onCustomAnswerChange, onCustomAnswerConfirm, showCustomAnswer = true, customAnswerPlaceholder = "Type your answer…", showSkip = true, onSkip, onClose, onNext, submitTooltip, onPrevious, currentStep, totalSteps, renderPreview, autoFocus = false, enableShortcuts = false, className }) => {
	const [isCustomExpanded, setIsCustomExpanded] = useState(false);
	const [activeRow, setActiveRow] = useState(0);
	const [pendingId, setPendingId] = useState(null);
	const rootRef = useRef(null);
	const rowsRef = useRef([]);
	const customInputRef = useRef(null);
	const isCustomSelected = isCustomExpanded || customAnswer.length > 0;
	const hasSteps = currentStep != null && totalSteps != null;
	const showStepControls = hasSteps && totalSteps > 1;
	const showSkipButton = showSkip && onSkip != null;
	const chosenId = multiSelect ? null : pendingId ?? selectedOptionIds[0] ?? null;
	const showSubmitButton = multiSelect || isCustomSelected || chosenId !== null;
	const showFooter = showStepControls || showSkipButton || showSubmitButton;
	const progressValue = hasSteps ? currentStep / totalSteps * 100 : 0;
	const isFirstStep = currentStep === 1;
	const isLastStep = hasSteps && currentStep === totalSteps;
	const numberedCount = Math.min(options.length, 9);
	const otherDigit = showCustomAnswer && options.length < 9 ? options.length + 1 : null;
	const rowCount = options.length + (showCustomAnswer ? 1 : 0);
	const tabbableRow = activeRow < rowCount ? activeRow : 0;
	const handleOptionToggle = (optionId) => {
		if (multiSelect) {
			const nextIds = selectedOptionIds.includes(optionId) ? selectedOptionIds.filter((id) => id !== optionId) : [...selectedOptionIds, optionId];
			onSelectionChange?.(nextIds);
		} else {
			setIsCustomExpanded(false);
			setPendingId(null);
			onCustomAnswerChange?.("");
			onSelectionChange?.([optionId]);
		}
	};
	const handleOptionKey = (optionId) => {
		if (multiSelect) handleOptionToggle(optionId);
		else {
			setIsCustomExpanded(false);
			onCustomAnswerChange?.("");
			setPendingId(optionId);
		}
	};
	useAltDigitHotkey({
		count: numberedCount,
		onDigit: (index) => handleOptionKey(options[index].id),
		enabled: enableShortcuts,
		ref: rootRef
	});
	useEffect(() => {
		if (!autoFocus || isFieldWithText(document.activeElement)) return;
		rootRef.current?.focus({ preventScroll: true });
	}, [autoFocus]);
	const focusRow = (index) => {
		setActiveRow(index);
		rowsRef.current[index]?.focus();
	};
	/**
	* The card's own keys, never fired from a text field inside it; a held key acts once.
	*
	* 1. ↑ / ↓ move focus; a digit picks or toggles; Enter on a row selects or toggles it.
	* 2. ⌘/Ctrl+Enter sends: the selected option (single-select) or the next step; Space skips.
	* 3. Any other character starts a custom answer: focus moves to "Other", which receives it.
	* 4. A key the card takes stops here, so page shortcuts (`a`, `/`) never see it.
	*/
	const handleCardKeyDown = (e) => {
		if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && !e.altKey && !e.repeat) {
			if (!showSubmitButton) return;
			send();
			e.preventDefault();
			e.stopPropagation();
			return;
		}
		if (e.key === "ArrowUp" && e.target === customInputRef.current && !hasCommandModifier(e)) {
			e.preventDefault();
			focusRow(options.length);
			return;
		}
		if (e.defaultPrevented || hasCommandModifier(e) || isEditableTarget(e.target)) return;
		if ((e.key === "ArrowDown" || e.key === "ArrowUp") && rowCount > 0) {
			e.preventDefault();
			const delta = e.key === "ArrowDown" ? 1 : -1;
			const target = e.target;
			const current = rowsRef.current.findIndex((row) => target instanceof Node && !!row?.contains(target));
			focusRow(((current === -1 ? delta > 0 ? -1 : rowCount : current) + delta + rowCount) % rowCount);
			return;
		}
		if (e.repeat) return;
		const digit = digitFromKeyCode(e.code);
		if (digit !== null && digit === otherDigit) {
			e.preventDefault();
			e.stopPropagation();
			customInputRef.current?.focus();
			return;
		}
		if (digit !== null && digit <= numberedCount) {
			e.preventDefault();
			e.stopPropagation();
			handleOptionKey(options[digit - 1].id);
			return;
		}
		if (e.key === " " && e.target === e.currentTarget) {
			e.preventDefault();
			e.stopPropagation();
			if (showSkipButton) onSkip?.();
			return;
		}
		if (showCustomAnswer && digit === null && e.key.length === 1 && e.key !== " ") {
			e.stopPropagation();
			customInputRef.current?.focus();
			return;
		}
	};
	const send = () => {
		if (!multiSelect && !isCustomSelected && chosenId) handleOptionToggle(chosenId);
		else onNext?.();
	};
	const handleNextStep = () => {
		if (!multiSelect && pendingId) handleOptionToggle(pendingId);
		else onNext?.();
	};
	const handleCustomToggle = (checked) => {
		setIsCustomExpanded(checked);
		if (checked) {
			if (!multiSelect) onSelectionChange?.([]);
		} else onCustomAnswerChange?.("");
	};
	const handleCustomFocus = () => {
		setIsCustomExpanded(true);
		setPendingId(null);
		if (!multiSelect) onSelectionChange?.([]);
	};
	const handleCustomRowClick = () => {
		if (multiSelect) handleCustomToggle(!isCustomSelected);
		else handleCustomFocus();
	};
	const handleCustomRowKey = () => {
		const opens = !multiSelect || !isCustomSelected;
		handleCustomRowClick();
		if (opens) customInputRef.current?.focus();
	};
	const submitButton = /* @__PURE__ */ jsxs(Button, {
		variant: "primary",
		size: "small",
		onClick: send,
		"data-testid": "question-next-button",
		children: [isLastStep ? "Submit" : "Next", /* @__PURE__ */ jsx(Kbd, {
			"aria-hidden": true,
			casing: "as-written",
			className: SEND_KEY_CLASS,
			children: `${keys.mod.symbol} Enter`
		})]
	});
	return /* @__PURE__ */ jsxs("div", {
		ref: rootRef,
		role: "group",
		"aria-label": question,
		tabIndex: -1,
		onKeyDown: handleCardKeyDown,
		className: cn("tw:flex tw:max-w-[800px] tw:flex-col tw:gap-4 tw:overflow-hidden tw:rounded-xl tw:border tw:border-solid tw:border-border tw:bg-card tw:text-card-foreground tw:shadow-lg tw:outline-none", className),
		"data-testid": "question-root",
		"data-question-step": currentStep,
		"data-question-total-steps": totalSteps,
		"data-question-multi-select": multiSelect,
		children: [
			/* @__PURE__ */ jsxs("div", {
				className: "tw:flex tw:items-start tw:gap-2 tw:pl-4 tw:pr-2 tw:pt-4",
				children: [/* @__PURE__ */ jsxs("div", {
					className: "tw:flex tw:min-w-0 tw:flex-1 tw:flex-col tw:gap-1",
					children: [/* @__PURE__ */ jsx("p", {
						className: "tw:m-0 tw:text-base tw:font-semibold tw:leading-6",
						children: question
					}), header && /* @__PURE__ */ jsx("p", {
						className: "tw:m-0 tw:text-xs tw:leading-4 tw:text-muted-foreground",
						children: header
					})]
				}), onClose && /* @__PURE__ */ jsx(Button, {
					variant: "outline",
					size: "icon-sm",
					"aria-label": "Close",
					onClick: onClose,
					"data-testid": "question-close-button",
					children: /* @__PURE__ */ jsx(X, { className: "tw:size-4" })
				})]
			}),
			/* @__PURE__ */ jsx("div", {
				className: "tw:px-4",
				children: /* @__PURE__ */ jsxs("div", {
					className: "tw:flex tw:flex-col tw:gap-4 tw:border-0 tw:border-l tw:border-solid tw:border-primary tw:px-4 tw:py-2",
					children: [options.map((option, index) => {
						const digit = index < numberedCount ? index + 1 : null;
						const isSelected = selectedOptionIds.includes(option.id);
						return /* @__PURE__ */ jsxs("div", {
							ref: (row) => {
								rowsRef.current[index] = row;
							},
							className: "tw:-mx-2 tw:-my-1.5 tw:flex tw:cursor-pointer tw:items-start tw:gap-2 tw:rounded-md tw:px-2 tw:py-1.5 tw:outline-none tw:hover:bg-accent tw:focus-visible:ring-2 tw:focus-visible:ring-ring",
							onClick: () => handleOptionToggle(option.id),
							onKeyDown: (e) => {
								if (hasCommandModifier(e) || e.repeat) return;
								if (e.key === " " || e.key === "Enter") {
									e.preventDefault();
									handleOptionKey(option.id);
								}
							},
							onFocus: () => setActiveRow(index),
							role: "button",
							"aria-pressed": multiSelect ? isSelected : chosenId === option.id,
							tabIndex: index === tabbableRow ? 0 : -1,
							"aria-keyshortcuts": digit === null ? void 0 : [String(digit), enableShortcuts && `Alt+${digit}`].filter(Boolean).join(" "),
							"data-question-option": true,
							"data-testid": `question-option-${option.id}`,
							children: [/* @__PURE__ */ jsx(OptionKey, {
								digit,
								checked: multiSelect ? isSelected : chosenId === option.id
							}), /* @__PURE__ */ jsxs("div", {
								className: "tw:flex tw:min-w-0 tw:flex-1 tw:items-start tw:gap-3",
								children: [/* @__PURE__ */ jsxs("div", {
									className: "tw:flex tw:flex-col tw:gap-1.5",
									children: [/* @__PURE__ */ jsx("span", {
										className: "tw:text-sm tw:font-medium tw:leading-none",
										children: option.label
									}), option.description && /* @__PURE__ */ jsx("span", {
										className: "tw:text-sm tw:font-normal tw:leading-5 tw:text-muted-foreground",
										children: option.description
									})]
								}), option.preview && /* @__PURE__ */ jsx("div", {
									"data-testid": "question-option-preview",
									className: cn("tw:ml-auto tw:flex tw:max-h-40 tw:min-w-0 tw:max-w-[50%] tw:flex-col tw:overflow-hidden tw:rounded-md", !renderPreview && "tw:overflow-auto tw:border tw:border-solid tw:border-border tw:bg-muted tw:p-2 tw:text-foreground"),
									children: renderPreview ? renderPreview(option.preview) : /* @__PURE__ */ jsx("pre", {
										className: "tw:m-0 tw:overflow-visible tw:border-0 tw:bg-transparent tw:p-0 tw:font-mono tw:text-xs tw:leading-5 tw:text-foreground",
										children: option.preview
									})
								})]
							})]
						}, option.id);
					}), showCustomAnswer && /* @__PURE__ */ jsxs("div", {
						className: "tw:flex tw:flex-col tw:gap-2",
						children: [/* @__PURE__ */ jsxs("div", {
							ref: (row) => {
								rowsRef.current[options.length] = row;
							},
							className: "tw:-mx-2 tw:-my-1.5 tw:flex tw:cursor-pointer tw:items-start tw:gap-2 tw:rounded-md tw:px-2 tw:py-1.5 tw:outline-none tw:hover:bg-accent tw:focus-visible:ring-2 tw:focus-visible:ring-ring",
							onClick: handleCustomRowClick,
							onKeyDown: handleKeyDown(handleCustomRowKey),
							onFocus: () => setActiveRow(options.length),
							role: "button",
							"aria-pressed": isCustomSelected,
							"aria-keyshortcuts": otherDigit === null ? void 0 : String(otherDigit),
							tabIndex: options.length === tabbableRow ? 0 : -1,
							children: [/* @__PURE__ */ jsx(OptionKey, {
								digit: otherDigit,
								checked: isCustomSelected
							}), /* @__PURE__ */ jsx("span", {
								className: cn("tw:flex-1 tw:text-sm tw:font-medium tw:leading-none", !multiSelect && isCustomSelected && "tw:text-primary"),
								children: "Other"
							})]
						}), /* @__PURE__ */ jsx(TextInput, {
							ref: customInputRef,
							value: customAnswer,
							onValueChange: (value) => onCustomAnswerChange?.(value),
							onFocus: handleCustomFocus,
							onKeyDown: onCustomAnswerConfirm ? (e) => {
								if (e.key === "Enter" && !e.shiftKey && !(e.metaKey || e.ctrlKey)) {
									e.preventDefault();
									onCustomAnswerConfirm();
								}
							} : void 0,
							placeholder: customAnswerPlaceholder,
							sizing: "small",
							testId: "question-custom"
						})]
					})]
				})
			}),
			numberedCount > 0 && /* @__PURE__ */ jsxs("div", {
				"aria-hidden": true,
				className: "tw:-mt-1 tw:flex tw:flex-wrap tw:items-center tw:gap-x-3 tw:gap-y-1 tw:px-4 tw:text-xs tw:text-muted-foreground",
				"data-testid": "question-keyboard-hint",
				children: [
					/* @__PURE__ */ jsxs("span", {
						className: "tw:inline-flex tw:items-center tw:gap-1",
						children: [/* @__PURE__ */ jsx(Kbd, {
							casing: "as-written",
							children: keyRange(numberedCount + (otherDigit === null ? 0 : 1))
						}), multiSelect ? "toggle" : "choose"]
					}),
					rowCount > 1 && /* @__PURE__ */ jsxs("span", {
						className: "tw:inline-flex tw:items-center tw:gap-1",
						children: [/* @__PURE__ */ jsxs(Kbd, { children: [/* @__PURE__ */ jsx(ArrowUp, { className: "tw:size-3" }), /* @__PURE__ */ jsx(ArrowDown, { className: "tw:size-3" })] }), "move"]
					}),
					/* @__PURE__ */ jsxs("span", {
						className: "tw:inline-flex tw:items-center tw:gap-1",
						children: [/* @__PURE__ */ jsx(Kbd, {
							casing: "as-written",
							children: "Enter"
						}), multiSelect ? "toggle" : "select"]
					})
				]
			}),
			/* @__PURE__ */ jsxs("div", {
				className: "tw:flex tw:flex-col",
				children: [showStepControls && /* @__PURE__ */ jsx(Progress, {
					size: "sm",
					value: progressValue,
					className: "tw:rounded-none"
				}), showFooter && /* @__PURE__ */ jsxs("div", {
					className: cn("tw:flex tw:items-center tw:justify-end tw:gap-4 tw:px-4 tw:py-3", !showStepControls && "tw:border-0 tw:border-t tw:border-solid tw:border-border"),
					children: [
						showStepControls && /* @__PURE__ */ jsxs("div", {
							className: "tw:flex tw:items-center tw:gap-2",
							children: [
								/* @__PURE__ */ jsx("button", {
									type: "button",
									disabled: isFirstStep,
									className: "tw:flex tw:cursor-pointer tw:items-center tw:justify-center tw:bg-transparent tw:p-0 tw:disabled:cursor-default tailwindcss-preflight",
									onClick: onPrevious,
									"aria-label": "Previous step",
									children: /* @__PURE__ */ jsx(ChevronLeft, { className: cn("tw:size-5", isFirstStep ? "tw:text-primary/25" : "tw:text-primary") })
								}),
								/* @__PURE__ */ jsxs("span", {
									className: "tw:text-xs tw:leading-4 tw:text-muted-foreground",
									children: [
										currentStep,
										" of ",
										totalSteps
									]
								}),
								/* @__PURE__ */ jsx("button", {
									type: "button",
									disabled: currentStep === totalSteps,
									className: "tw:flex tw:cursor-pointer tw:items-center tw:justify-center tw:bg-transparent tw:p-0 tw:disabled:cursor-default tailwindcss-preflight",
									onClick: handleNextStep,
									"aria-label": "Next step",
									children: /* @__PURE__ */ jsx(ChevronRight, { className: cn("tw:size-5", currentStep === totalSteps ? "tw:text-primary/25" : "tw:text-primary") })
								})
							]
						}),
						showSkipButton && /* @__PURE__ */ jsx(Tooltip, {
							tooltip: /* @__PURE__ */ jsx(Kbd, {
								variant: "primary",
								casing: "as-written",
								children: "Space"
							}),
							placement: "top",
							triggerAsChild: true,
							children: /* @__PURE__ */ jsx(Button, {
								variant: "outline",
								size: "small",
								onClick: onSkip,
								"aria-keyshortcuts": "Space",
								"data-testid": "question-skip-button",
								children: "Skip"
							})
						}),
						showSubmitButton && (submitTooltip ? /* @__PURE__ */ jsx(Tooltip, {
							tooltip: submitTooltip,
							placement: "top",
							triggerAsChild: true,
							children: submitButton
						}) : submitButton)
					]
				})]
			})
		]
	});
};
//#endregion
//#region src/blocks/AIKit/QuestionBlock.tsx
const QuestionBlock = ({ steps, showSkip = true, onSubmit, onClose, renderPreview, className }) => {
	const [currentStep, setCurrentStep] = useState(1);
	const [selections, setSelections] = useState({});
	const [customAnswers, setCustomAnswers] = useState({});
	const stepData = steps[currentStep - 1];
	const isLastStep = currentStep === steps.length;
	const advance = (updatedSelections) => {
		const finalSelections = updatedSelections ?? selections;
		if (isLastStep) onSubmit?.({
			selections: finalSelections,
			customAnswers
		});
		else setCurrentStep((s) => Math.min(s + 1, steps.length));
	};
	const handleSelectionChange = (ids) => {
		const updated = {
			...selections,
			[currentStep]: ids
		};
		setSelections(updated);
		if (!stepData.multiSelect) advance(updated);
	};
	return /* @__PURE__ */ jsx(Question, {
		...stepData,
		selectedOptionIds: selections[currentStep] ?? [],
		onSelectionChange: handleSelectionChange,
		customAnswer: customAnswers[currentStep] ?? "",
		onCustomAnswerChange: (value) => setCustomAnswers((prev) => ({
			...prev,
			[currentStep]: value
		})),
		currentStep,
		totalSteps: steps.length,
		showSkip: showSkip && !isLastStep,
		onNext: () => advance(),
		onPrevious: () => setCurrentStep((s) => Math.max(s - 1, 1)),
		onSkip: () => setCurrentStep((s) => Math.min(s + 1, steps.length)),
		onClose,
		renderPreview,
		className
	}, currentStep);
};
//#endregion
//#region src/blocks/AIKit/SuggestedPrompt.tsx
const SuggestedPrompt = ({ label, disabled, onClick, icon, variant = "row", className }) => {
	return /* @__PURE__ */ jsxs("button", {
		type: "button",
		disabled,
		"data-variant": variant,
		className: cn("tailwindcss-preflight tw:flex tw:items-center tw:justify-start tw:gap-2.5 tw:whitespace-nowrap", "tw:text-left tw:text-sm tw:text-foreground tw:transition-colors", "tw:focus-visible:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-ring tw:focus-visible:ring-offset-1", "tw:disabled:cursor-not-allowed tw:disabled:opacity-50", variant === "bubble" ? [
			"tw:w-fit tw:max-w-[min(85%,40rem)] tw:rounded-xl tw:px-3 tw:py-2",
			"tw:border tw:border-solid tw:border-border tw:bg-card",
			"tw:not-disabled:hover:bg-muted tw:focus-visible:bg-muted"
		] : "tw:rounded-lg tw:px-3 tw:py-2 tw:font-medium tw:not-disabled:hover:bg-accent", className),
		onClick,
		children: [icon ?? /* @__PURE__ */ jsx(Sparkles, { className: "tw:size-4 tw:shrink-0 tw:basis-4 tw:[&_*]:stroke-aigradient-600" }), /* @__PURE__ */ jsx("div", {
			className: "tw:whitespace-normal tw:text-left",
			children: label
		})]
	});
};
//#endregion
//#region src/blocks/AIKit/ThinkingIndicator.tsx
/**
* The agent's turn is in flight and has nothing to show yet.
*
* - Quiet chrome, matching the step rows it precedes (muted ink, no italic), so the hand-off
*   from "Thinking..." to the first step is seamless.
* - `role="status"` because a spinner is silence to a screen reader, and for much of a turn
*   this is all a transcript shows. The label is the announcement, so it is never decorative.
* - The icon is hidden from the tree: it says the same thing as the label beside it.
*/
const ThinkingIndicator = ({ label = "Thinking..." }) => {
	return /* @__PURE__ */ jsxs("div", {
		className: "tw:flex tw:items-center tw:gap-1.5",
		role: "status",
		"data-testid": "chat-thinking-indicator",
		children: [/* @__PURE__ */ jsx(Loader2, {
			"aria-hidden": true,
			className: "tw:size-3.5 tw:animate-spin tw:text-muted-foreground"
		}), /* @__PURE__ */ jsx("span", {
			className: "tw:text-xs tw:font-medium tw:text-muted-foreground",
			children: label
		})]
	});
};
//#endregion
//#region src/blocks/AIKit/ToolCall/ToolCallApproval.tsx
const STATUS_BADGE = {
	pending: {
		label: "Needs approval",
		color: "blue"
	},
	approved: {
		label: "Approved",
		color: "green"
	},
	rejected: {
		label: "Rejected",
		color: "red"
	},
	expired: {
		label: "Expired",
		color: "gray"
	}
};
/**
* Per-tool-call approve/reject frame — the granular sibling of `PlanApproval`
* (which approves a whole plan). Chrome + status + actions only: the app
* renders the call detail as `children` and owns the SDK/permission wiring.
*/
const ToolCallApproval = ({ toolName, title, status, onApprove, onReject, children, actions, isSubmitting = false, enableShortcuts = false, approveLabel = "Approve", rejectLabel = "Reject", className }) => {
	const isPending = status === "pending";
	const hasCustomActions = actions != null;
	const showActions = isPending && (isPending && !hasCustomActions && Boolean(onApprove || onReject) || hasCustomActions);
	const badge = STATUS_BADGE[status];
	useEffect(() => {
		if (!enableShortcuts || !isPending || isSubmitting) return;
		const isEditableTarget = (target) => {
			if (!(target instanceof HTMLElement)) return false;
			return target.isContentEditable || target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.tagName === "SELECT";
		};
		const handleKeyDown = (event) => {
			if (event.repeat || event.defaultPrevented || isEditableTarget(event.target)) return;
			if (event.key === "Enter" && !event.shiftKey && !event.ctrlKey && !event.metaKey && onApprove) {
				event.preventDefault();
				onApprove();
			} else if (event.key === "Escape" && onReject) {
				event.preventDefault();
				onReject();
			}
		};
		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [
		enableShortcuts,
		isPending,
		isSubmitting,
		onApprove,
		onReject
	]);
	return /* @__PURE__ */ jsxs("section", {
		"data-testid": "tool-call-approval",
		"data-status": status,
		"aria-label": typeof title === "string" ? title : toolName,
		className: cn("tw:flex tw:w-full tw:max-w-[800px] tw:flex-col tw:gap-3 tw:overflow-hidden tw:rounded-xl tw:border tw:border-solid tw:border-border tw:bg-muted tw:p-4", className),
		children: [
			/* @__PURE__ */ jsxs("div", {
				className: "tw:flex tw:items-center tw:gap-2",
				children: [/* @__PURE__ */ jsx(Badge, {
					color: badge.color,
					variant: "pill",
					className: "tw:normal-case tw:tracking-normal",
					children: badge.label
				}), /* @__PURE__ */ jsx("code", {
					className: "tw:rounded tw:bg-background tw:px-1.5 tw:py-0.5 tw:font-mono tw:text-xs tw:text-foreground",
					children: toolName
				})]
			}),
			title != null && /* @__PURE__ */ jsx("p", {
				className: "tw:m-0 tw:text-sm tw:font-medium tw:text-foreground",
				children: title
			}),
			children,
			showActions && /* @__PURE__ */ jsx("div", {
				className: "tw:flex tw:items-center tw:justify-end tw:gap-2 tw:overflow-x-auto",
				children: actions ?? /* @__PURE__ */ jsxs(Fragment$1, { children: [onReject && /* @__PURE__ */ jsx(Button, {
					variant: "outline",
					size: "small",
					onClick: onReject,
					disabled: isSubmitting,
					className: "tw:shrink-0 tw:whitespace-nowrap",
					children: rejectLabel
				}), onApprove && /* @__PURE__ */ jsx(Button, {
					variant: "primary",
					size: "small",
					onClick: onApprove,
					disabled: isSubmitting,
					className: "tw:shrink-0 tw:whitespace-nowrap",
					children: approveLabel
				})] })
			})
		]
	});
};
//#endregion
//#region src/blocks/AIKit/ToolCall/ToolCallChip.tsx
const chipClasses = cva(["tw:inline-flex tw:items-center tw:gap-1.5 tw:rounded-md tw:border tw:border-solid", "tw:border-border tw:bg-muted tw:px-2 tw:py-1 tw:text-xs tw:text-foreground"], {
	variants: { interactive: {
		true: "tw:cursor-pointer tw:focus-visible:outline-hidden tw:focus-visible:ring-2 tw:focus-visible:ring-ring tw:not-disabled:hover:bg-accent",
		false: ""
	} },
	defaultVariants: { interactive: false }
});
const StateIcon = ({ state }) => {
	if (state === "processing") return /* @__PURE__ */ jsx(Loader2, { className: "tw:size-3.5 tw:shrink-0 tw:animate-spin tw:text-muted-foreground" });
	if (state === "error") return /* @__PURE__ */ jsx(CircleX, { className: "tw:size-3.5 tw:shrink-0 tw:text-error-500" });
	return /* @__PURE__ */ jsx(CircleCheck, { className: "tw:size-3.5 tw:shrink-0 tw:text-primary" });
};
/**
* Presentational chip summarising a single tool call (icon + label + optional
* trailing action). It has **no** URL/router knowledge — wrap it in a `Link`
* or pass `onClick` for navigation; the app owns deep-linking.
*/
const ToolCallChip = ({ label, state, icon, trailing, onClick, className }) => {
	const content = /* @__PURE__ */ jsxs(Fragment$1, { children: [
		icon ?? /* @__PURE__ */ jsx(StateIcon, { state }),
		/* @__PURE__ */ jsx("span", {
			className: "tw:min-w-0 tw:truncate",
			children: label
		}),
		trailing != null && /* @__PURE__ */ jsx("span", {
			className: "tw:ml-auto tw:inline-flex tw:shrink-0 tw:items-center",
			children: trailing
		})
	] });
	if (onClick) return /* @__PURE__ */ jsx("button", {
		type: "button",
		"data-testid": "tool-call-chip",
		"data-state": state,
		onClick,
		className: cn("tailwindcss-preflight", chipClasses({ interactive: true }), className),
		children: content
	});
	return /* @__PURE__ */ jsx("span", {
		"data-testid": "tool-call-chip",
		"data-state": state,
		className: cn(chipClasses({ interactive: false }), className),
		children: content
	});
};
//#endregion
//#region src/blocks/AIKit/ToolCallStatusHeader.tsx
const headerVariants = cva("tw:flex tw:items-center tw:justify-between tw:gap-2 tw:px-2.5 tw:py-1.5", {
	variants: { status: {
		processing: "tw:border-b tw:border-border/60",
		warning: "tw:bg-warning/10",
		success: "tw:border-b tw:border-border/60",
		error: "tw:bg-destructive/10"
	} },
	defaultVariants: { status: "success" }
});
const statusInk = {
	processing: "tw:text-muted-foreground",
	warning: "tw:text-warning",
	success: "tw:text-muted-foreground",
	error: "tw:text-destructive"
};
const statusIconInk = {
	processing: "tw:text-muted-foreground",
	warning: "tw:text-warning",
	success: "tw:text-success/70",
	error: "tw:text-destructive"
};
const ToolCallStatusHeader = ({ icon: IconComponent, iconSpin, status, text, needsConfirmation, isCollapsible, isCollapsed, onToggleCollapse }) => {
	return /* @__PURE__ */ jsxs("div", {
		role: isCollapsible ? "button" : void 0,
		tabIndex: isCollapsible ? 0 : void 0,
		"aria-expanded": isCollapsible ? !isCollapsed : void 0,
		"data-status": status,
		className: cn(headerVariants({ status }), isCollapsible && "tw:cursor-pointer"),
		onClick: isCollapsible ? onToggleCollapse : void 0,
		onKeyDown: isCollapsible ? (e) => {
			if (e.key === "Enter" || e.key === " ") {
				e.preventDefault();
				onToggleCollapse?.();
			}
		} : void 0,
		children: [/* @__PURE__ */ jsxs("div", {
			className: "tw:flex tw:min-w-0 tw:flex-1 tw:items-center tw:gap-1.5",
			children: [/* @__PURE__ */ jsx(IconComponent, { className: cn("tw:size-3.5 tw:shrink-0", statusIconInk[status], iconSpin && "tw:animate-spin") }), /* @__PURE__ */ jsx("span", {
				"data-testid": "tool-status-text",
				className: cn("tw:truncate tw:pr-0.5 tw:text-xs tw:font-medium", needsConfirmation ? "tw:text-foreground" : statusInk[status]),
				children: text
			})]
		}), (needsConfirmation || isCollapsible) && /* @__PURE__ */ jsxs("div", {
			className: "tw:flex tw:shrink-0 tw:items-center tw:gap-2",
			children: [needsConfirmation && /* @__PURE__ */ jsxs("div", {
				className: "tw:flex tw:items-center tw:gap-1",
				children: [/* @__PURE__ */ jsx("span", {
					"data-testid": "tool-confirmation-required",
					className: "tw:text-xs tw:font-medium tw:text-warning",
					children: "Confirmation required"
				}), /* @__PURE__ */ jsx(TriangleAlert, { className: "tw:size-3.5 tw:text-warning" })]
			}), isCollapsible && /* @__PURE__ */ jsx(ChevronDown, { className: cn("tw:size-3.5 tw:shrink-0 tw:text-muted-foreground", "tw:transition-transform tw:duration-300 tw:ease-out tw:motion-reduce:transition-none", !isCollapsed && "tw:rotate-180") })]
		})]
	});
};
//#endregion
//#region src/blocks/AIKit/usageStatsHelpers.ts
/**
* Clamp a percentage to 0–100 and normalize non-finite input to 0.
*
* - `Progress`'s indicator is unclamped, so a value over 100 slides the bar off-screen.
* - `NaN` normalizes to 0 — `Math.min`/`Math.max` propagate it, and it renders as no bar at all.
*/
const clampPercentage = (value) => Number.isFinite(value) ? Math.min(100, Math.max(0, value)) : 0;
/**
* A percentage as text — `72%`, `0.2%`, `99%`.
*
* - Whole numbers from 1% up; a decimal only below 1%, where rounding would read as `0%`.
* - A non-zero share never prints as `0.0%` — it floors at `0.1%`.
* - Never `100%` for a value under 100: the text would disagree with the at-limit state computed
*   from the raw value, exactly at the boundary everything else turns on.
*/
const formatPercentage = (value) => {
	const oneDecimal = Math.round(value * 10) / 10;
	if (value > 0 && oneDecimal < 1) return `${Math.max(.1, oneDecimal).toFixed(1)}%`;
	const rounded = Math.round(value);
	return `${value < 100 ? Math.min(rounded, 99) : rounded}%`;
};
//#endregion
//#region src/blocks/AIKit/UsageBar.tsx
/**
* One budget, as a labelled row over a bar.
*
* - `value` is clamped by `clampPercentage` — `Progress`'s indicator is unclamped.
*/
const UsageBar = ({ value, label, variant, className, ...props }) => /* @__PURE__ */ jsxs("span", {
	...props,
	className: cn("tw:flex tw:flex-col tw:gap-1.5 tw:text-xs tw:text-muted-foreground", className),
	children: [/* @__PURE__ */ jsxs("span", {
		className: "tw:flex tw:items-baseline tw:justify-between tw:gap-2",
		children: [/* @__PURE__ */ jsx("span", { children: label }), /* @__PURE__ */ jsx("span", { children: `${formatPercentage(Math.max(0, 100 - value))} left` })]
	}), /* @__PURE__ */ jsx(Progress, {
		size: "sm",
		variant,
		value: clampPercentage(value)
	})]
});
//#endregion
//#region src/blocks/AIKit/UsageStatsRing.tsx
const UsageStatsRing = ({ value, size = 16, strokeWidth = 2.5, className }) => {
	const radius = Math.max(0, (size - strokeWidth) / 2 - 1);
	const circumference = 2 * Math.PI * radius;
	const offset = circumference * (1 - clampPercentage(value) / 100);
	return /* @__PURE__ */ jsxs("svg", {
		width: size,
		height: size,
		viewBox: `0 0 ${size} ${size}`,
		className: cn("tw:-rotate-90", className),
		role: "presentation",
		children: [/* @__PURE__ */ jsx("circle", {
			cx: size / 2,
			cy: size / 2,
			r: radius,
			fill: "none",
			strokeWidth,
			className: "tw:stroke-muted-foreground tw:opacity-50"
		}), /* @__PURE__ */ jsx("circle", {
			cx: size / 2,
			cy: size / 2,
			r: radius,
			fill: "none",
			strokeWidth,
			strokeLinecap: "round",
			strokeDasharray: circumference,
			strokeDashoffset: offset,
			className: "tw:stroke-primary tw:transition-all"
		})]
	});
};
//#endregion
//#region src/blocks/AIKit/UsageStats.tsx
const UsageStats = ({ value, alert = false, children, className, ariaLabel, align = "end", side = "top", sideOffset = 8, open, onOpenChange }) => {
	const hasAlert = Boolean(alert);
	const isWarning = alert === "warning";
	const defaultLabel = `Usage: ${Math.round(clampPercentage(value))}%${hasAlert ? " (usage alert)" : ""}`;
	return /* @__PURE__ */ jsxs(HoverCard, {
		open,
		onOpenChange,
		children: [/* @__PURE__ */ jsx(HoverCard.Trigger, {
			asChild: true,
			children: /* @__PURE__ */ jsxs("button", {
				type: "button",
				"aria-label": ariaLabel ?? defaultLabel,
				className: cn("tw:relative tw:inline-flex tw:size-8 tw:shrink-0 tw:items-center tw:justify-center", "tw:cursor-pointer tw:rounded-full tw:border-0 tw:bg-transparent tw:p-0", "tw:transition-colors tw:hover:bg-muted", "tw:focus-visible:outline-hidden tw:focus-visible:ring-2 tw:focus-visible:ring-ring/50", className),
				children: [/* @__PURE__ */ jsx(UsageStatsRing, { value }), hasAlert && /* @__PURE__ */ jsx(AlertCircle, {
					"data-testid": "usage-stats-alert",
					className: cn("tw:absolute tw:-right-0.5 tw:-top-0.5 tw:size-3", isWarning ? "tw:text-warning-500" : "tw:text-error-500")
				})]
			})
		}), /* @__PURE__ */ jsx(HoverCard.Content, {
			align,
			side,
			sideOffset,
			children
		})]
	});
};
//#endregion
export { AnswerActions, AnswerSummary, ChatInlineIcon, CollapsibleStepHeader, DIAGRAM_LANGUAGES, FileDownloadChip, InlineStepDetail, InlineStepGroup, InlineStepLabel, Markdown, MarkdownTable, MessageBubble, PROMPT_INPUT_CONTAINER, PlanApproval, PlanRewritingIndicator, PromptInput, Question, QuestionBlock, SuggestedPrompt, ThinkingIndicator, ToolCallApproval, ToolCallChip, ToolCallStatusHeader, UsageBar, UsageStats, UsageStatsRing, clampPercentage, findMentionToken, findSlashToken, formatPercentage, getChatInlineIconName };

//# sourceMappingURL=AIKit-BVCWP9Ks.js.map